import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchMessages, loadOlderMessages, fetchMessagesAroundTarget, sendMessage, addMessage, addOptimisticMessage, updateMessageStatus, removeOptimisticMessage, setTypingUser, addReaction, markChannelAsRead, removeMessage, setCurrentChannel, clearDateRangeSearch, fetchUnreadCounts, fetchMessagesByDateRange, invalidateAroundMessageFetches } from '../../store/slices/chatSlice';
import { store } from '../../store';
import { addNotification, setChannelSoundPreference, clearChatMappingTarget, setLastChatMapping } from '../../store/slices/uiSlice';
import { createTask, fetchTasks, fetchTasksByMapTaskId } from '../../store/slices/taskSlice';
import { messageHasTaskContent, descriptionHtmlFromMessage, resolveParentTaskIdForWorkflow } from '../../utils/chatMappingUtils';
import { fetchSavedMessagesByChannel } from '../../store/slices/savedMessageSlice';
import websocketService from '../../services/websocketService';
import notificationService from '../../services/notificationService';
import { acknowledgeMessageDelivered } from '../../services/messageDeliveryAck';
import { parseDeliveryFlagsFromPayload } from '../../utils/messageDeliveryStatus';
import { isOwnReactionBroadcast, pickChatTranslationFields, pickSensitiveMessageFields, shouldAnnounceIncomingMessage } from '../../utils/websocketMessageUtils';
import { isDirectMessageChannelType } from '../../utils/channelTypeUtils';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import AttendanceActions from '../attendance/AttendanceActions';
import LogToWorkModal from '../attendance/LogToWorkModal';
import SharedUserLeaveStatusModal from '../attendance/SharedUserLeaveStatusModal';
import DayActivityTimelineModal from '../attendance/DayActivityTimelineModal';
import { isAttendanceChannel as checkIsAttendanceChannel, isMyAttendanceTimelineMessage, fetchSharedWithMe, fetchDayActivityTimeline } from '../../services/attendanceService';
import { isNotificationsChannel as checkIsNotificationsChannel } from '../../utils/channelTypeUtils';
import {
  CHANNEL_NOTIFICATION_VISIBILITY_EVENT,
  isChannelNotificationsVisible,
  setChannelNotificationsVisible,
} from '../../utils/channelNotificationVisibilityPrefs';
import { formatLeaveStatusLabel } from '../../utils/loadSharedUserDayStatus';
import { formatPresenceLabel, presenceDotClass } from '../../utils/presenceLabelUtils';
import TypingIndicator from './TypingIndicator';
import UserProfileModal from '../common/UserProfileModal';
import EditChannelModal from './EditChannelModal';
import ForwardMessageModal from './ForwardMessageModal';
import ChatInChannelSearchPanel from './ChatInChannelSearchPanel';
import ConnectMeetingModal from './ConnectMeetingModal';
import ChannelCallMenu from './ChannelCallMenu';
import CloseMeetingDialog from './CloseMeetingDialog';
import CreateEventWizard from '../../features/events/components/CreateEventWizard';
import { eventDtoToTimelineInfo } from '../../features/events/utils/eventTimelineUtils';
import { upsertRealmDocs } from '../../realm';
import { cloneEvent, fetchEvent, recordEventForward } from '../../features/events/services/eventService';
import { useCompletionTimeDialog, messageIsWorkflowClosed } from './CompletionTimeDialog';
import { useUrgentDeadlineDialog } from './UrgentDeadlineDialog';
import { resolveWorkflowCloseMinutes, workflowLinkedTasksFrom, loadMappedByTaskId, apiErrorMessage } from '../../utils/workflowTaskUtils';
import { openLiveKitCallWindow } from '../../utils/liveKitCallWindow';
import RightSidebar from './RightSidebar';
import axios from 'axios';

import { API_URL } from '../../config/api';
import PresignedAvatar from '../common/PresignedAvatar';
import { MenuIcon, BookmarkIcon, CalendarIcon, EventsIcon } from './SidebarIcons';
import doodlePattern from '../../doodle-pattern.svg';
import { getTopLevelParentId } from '../../utils/threadUtils';
import { buildClosedMeetingMessage, findActiveChannelMeeting, getMeetingNoteMessages, getLiveKitRoomName, isAlreadyClosedError, isDirectCallMeeting, isMeetingOrganizer } from '../../utils/meetingUtils';
import { filterMessagesForChatSearch, getChatSearchMatchIds } from '../../utils/chatSearchUtils';
import { startDirectCall } from '../../services/liveKitService';
import { recordCallJoin } from '../../services/attendanceService';
import { WORKFLOW_CLOSED_STATUS_ID, isWorkflowReaction, getActiveWorkflowByCategory, WORK_ITEM_TYPE_TASK_ID, isWorkflowCreatingWorkItemType, WORKFLOW_URGENT_ID, resolveUrgentWorkflowReactionId, messageHasUrgentWorkflow, messageIsRaisedWorkflow } from '../../utils/reactionConfig';
import { resolveUsersByIds } from '../../realm/offlineLookups';
import { extractEntityId, findUserForDm, otherParticipantId, normalizeMemberIds } from '../../utils/directMessageUtils';
import { workloopInboxInitial, workloopInboxLabel } from '../../utils/organizationBrand';
import CreateWorkflowDialog from './CreateWorkflowDialog';
import { stripHtmlForTextExtraction } from '../../utils/fileUtils';

const notifyWorkflowsAssignedChanged = (detail) => {
  window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', { detail }));
};

const shouldHandleTimelineArrowKey = (event) => {
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return false;

  const node = event.target instanceof Element
    ? event.target
    : (document.activeElement instanceof Element ? document.activeElement : null);
  if (!node) return false;

  if (node.closest('input, textarea, select, [contenteditable="true"]')) return false;
  if (node.closest('.ql-editor')) return false;
  if (node.closest('[data-mention-suggestions], [role="listbox"], [role="menu"], [role="dialog"]')) {
    return false;
  }

  return Boolean(node.closest('[data-chat-timeline]'));
};

const ChatWindow = ({ channel, messageIdToScroll, threadIdToOpen, sidebarOpen, onToggleSidebar, dailyTasksOpen, calendarOpen, onThreadStateChange, setDailyTasksOpen, setCalendarOpen, onExpandDailyTasks, onOpenUserWorkload, workloadTargetUserId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { promptCompletionTime, completionTimeDialog } = useCompletionTimeDialog();
  const { promptUrgentDeadline, urgentDeadlineDialog } = useUrgentDeadlineDialog();
  const { user } = useSelector((state) => state.auth);
  const { messages, typingUsers, loading, dateRangeSearch } = useSelector((state) => state.chat);
  const { users: allUsers = [] } = useSelector((state) => state.users);
  const chatMappingTarget = useSelector((state) => state.ui.chatMappingTarget);

  const handleMapMessageToTarget = useCallback(async (message) => {
    if (!chatMappingTarget || !message?.id) return;

    const htmlContent = descriptionHtmlFromMessage(message);
    if (!messageHasTaskContent(message)) {
      dispatch(addNotification({
        type: 'error',
        message: 'Message content is empty. Cannot map to task.',
      }));
      return;
    }

    try {
      let parentTaskId = null;

      if (chatMappingTarget.type === 'workflow' && message.id === chatMappingTarget.messageId) {
        dispatch(addNotification({
          type: 'warning',
          message: 'Cannot map a workflow to its own message.',
        }));
        return;
      }

      if (chatMappingTarget.type === 'task') {
        parentTaskId = chatMappingTarget.id;
      } else if (chatMappingTarget.type === 'workflow') {
        parentTaskId = await resolveParentTaskIdForWorkflow(
          dispatch,
          fetchTasks,
          createTask,
          chatMappingTarget,
        );
      }

      if (!parentTaskId) {
        throw new Error('Could not resolve parent task for mapping');
      }

      await dispatch(createTask({
        title: '',
        description: htmlContent,
        messageId: message.id,
        mapTaskId: parentTaskId,
      })).unwrap();

      dispatch(setLastChatMapping({
        parentTaskId,
        type: chatMappingTarget.type,
        workflowMessageId: chatMappingTarget.type === 'workflow' ? chatMappingTarget.messageId : undefined,
        at: Date.now(),
      }));
      dispatch(clearChatMappingTarget());
      dispatch(addNotification({
        type: 'success',
        message: chatMappingTarget.type === 'workflow'
          ? 'Chat mapped to workflow'
          : 'Chat mapped to task',
      }));
    } catch (error) {
      const msg = error?.message || error?.error || 'Failed to map chat message';
      dispatch(addNotification({ type: 'error', message: msg }));
    }
  }, [chatMappingTarget, dispatch]);

  const syncUnreadCountsFromBackend = () => {
    setTimeout(() => {
      const state = store.getState();
      const channels = state.channels?.channels || [];
      const dms = state.channels?.directMessages || [];
      const channelIds = [...channels, ...dms].map((c) => c?.id).filter(Boolean);
      if (channelIds.length) {
        dispatch(fetchUnreadCounts({ channelIds, lastViewed: state.chat?.lastViewed }));
      }
    }, 800);
  };
  const { soundPreferences } = useSelector((state) => state.ui);
  const { savedMessagesByChannel, channelMetaByChannel } = useSelector((state) => state.savedMessages);
  const channelSavedCount = channel?.id
    ? (channelMetaByChannel[channel.id]?.totalElements ??
      (savedMessagesByChannel[channel.id] || []).length)
    : 0;
  const [isTyping, setIsTyping] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [activeThreadId, setActiveThreadId] = useState(null); // Track the parent message of the current thread view
  const [threadPinned, setThreadPinned] = useState(false);
  const [activeRightPanel, setActiveRightPanel] = useState(null); // Track custom active panels in the right sidebar (e.g. 'files')
  const [metricsExpanded, setMetricsExpanded] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const [filesExpanded, setFilesExpanded] = useState(false);
  const [createWorkflowDraft, setCreateWorkflowDraft] = useState(null);
  const [creatingWorkflow, setCreatingWorkflow] = useState(false);
  const [templatePosting, setTemplatePosting] = useState(null);
  const [replyingToMessage, setReplyingToMessage] = useState(null); // Track specific message being replied to in a thread
  const [showConnectMeeting, setShowConnectMeeting] = useState(false);
  const [startingDirectCall, setStartingDirectCall] = useState(false);
  const [expandCallNotesMeetingId, setExpandCallNotesMeetingId] = useState(null);
  const [showCloseCall, setShowCloseCall] = useState(false);
  const [closingCall, setClosingCall] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [messageToForward, setMessageToForward] = useState(null);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showEditChannel, setShowEditChannel] = useState(false);
  const [showInChatSearch, setShowInChatSearch] = useState(false);
  const [inChatSearchQuery, setInChatSearchQuery] = useState('');
  const [inChatMentionsOnly, setInChatMentionsOnly] = useState(false);
  const [inChatSearchFromUserId, setInChatSearchFromUserId] = useState('');
  const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const showScrollToBottomRef = useRef(false);
  const [hasTeamAttendanceShares, setHasTeamAttendanceShares] = useState(false);
  const [dmAttendanceShared, setDmAttendanceShared] = useState(false);
  const [dmDayStatus, setDmDayStatus] = useState(null);
  const [dmDayTimeline, setDmDayTimeline] = useState(null);
  const [openingDmTimeline, setOpeningDmTimeline] = useState(false);
  const [showEventWizard, setShowEventWizard] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [cloningEventId, setCloningEventId] = useState(null);

  // Sidebar personal calendar is full-screen in ChatApp — close conversation calendar to avoid conflict
  useEffect(() => {
    if (calendarOpen && activeRightPanel === 'calendar') {
      setActiveRightPanel(null);
    }
  }, [calendarOpen, activeRightPanel]);

  useEffect(() => {
    if (activeRightPanel !== 'managementMetrics') {
      setMetricsExpanded(false);
    }
    if (activeRightPanel !== 'events') {
      setEventsExpanded(false);
    }
    if (activeRightPanel !== 'files') {
      setFilesExpanded(false);
    }
  }, [activeRightPanel]);
  const typingTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const markedAsReadRef = useRef(new Set());
  const channelIdForReadRef = useRef(null); // track channel so we only clear marked set when channel changes
  const isLoadingOlderRef = useRef(false);
  const olderLoadFailedRef = useRef(false);
  const messageRefs = useRef({}); // Store refs for each message for scrolling
  const messagesForListRef = useRef([]);
  const jumpTimelineUpRef = useRef(() => {});
  const jumpTimelineDownRef = useRef(() => {});
  const menuRef = useRef(null);
  const isUserScrollingRef = useRef(false);
  const olderPageRef = useRef(1);
  const prevChannelIdForAutoScrollRef = useRef(null);
  const rawChannelMessagesRef = useRef([]);
  const duplicatePageStreakRef = useRef(0);
  const hasMoreMessagesRef = useRef(true);
  const isLoadingForNavigationRef = useRef(false);
  const messageNavigationGenerationRef = useRef(0);
  const activeThreadIdRef = useRef(null);
  const deepLinkNavKeyRef = useRef(null);
  const pendingSearchScrollRestoreRef = useRef(null);
  const skipNextSearchScrollRef = useRef(false);
  const pendingNavigationMessageIdRef = useRef(null);
  const aroundContextRef = useRef({ active: false, hasMoreBefore: false, hasMoreAfter: false });
  const aroundLoadInProgressRef = useRef(false);
  const pendingScrollMessageIdRef = useRef(null);
  const pendingDailyTasksNavRef = useRef(null);
  const pendingStickToBottomRef = useRef(false);
  const windowWasAwayRef = useRef(false);
  const timelineSmoothJumpUntilRef = useRef(0);
  const timelineContentRef = useRef(null);
  const stickMainTimelineToLatestRef = useRef(() => {});
  const threadPinnedRef = useRef(false);
  const threadHoverCloseTimerRef = useRef(null);
  const threadHoverOpenTimerRef = useRef(null);
  const rightPanelCollapsedRef = useRef(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const THREAD_HOVER_OPEN_MS = 1000;

  useEffect(() => {
    hasMoreMessagesRef.current = hasMoreMessages;
  }, [hasMoreMessages]);

  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    rightPanelCollapsedRef.current = rightPanelCollapsed;
  }, [rightPanelCollapsed]);

  useEffect(() => {
    setRightPanelCollapsed(false);
  }, [channel?.id]);

  const clearThreadHoverCloseTimer = () => {
    if (threadHoverCloseTimerRef.current) {
      clearTimeout(threadHoverCloseTimerRef.current);
      threadHoverCloseTimerRef.current = null;
    }
  };

  const clearThreadHoverOpenTimer = () => {
    if (threadHoverOpenTimerRef.current) {
      clearTimeout(threadHoverOpenTimerRef.current);
      threadHoverOpenTimerRef.current = null;
    }
  };

  const closeThreadPanel = () => {
    clearThreadHoverCloseTimer();
    clearThreadHoverOpenTimer();
    threadPinnedRef.current = false;
    setThreadPinned(false);
    setActiveThreadId(null);
    setReplyingToMessage(null);
    setRightPanelCollapsed(false);
  };

  const openPinnedThread = (threadId) => {
    if (!threadId) return;
    clearThreadHoverCloseTimer();
    clearThreadHoverOpenTimer();
    threadPinnedRef.current = true;
    setThreadPinned(true);
    setActiveThreadId(threadId);
    setRightPanelCollapsed(false);
  };

  const previewThread = (threadId) => {
    if (threadPinnedRef.current) return;
    if (rightPanelCollapsedRef.current) return;
    clearThreadHoverCloseTimer();
    if (threadId) {
      if (activeThreadIdRef.current === threadId) {
        clearThreadHoverOpenTimer();
        return;
      }
      clearThreadHoverOpenTimer();
      threadHoverOpenTimerRef.current = setTimeout(() => {
        threadHoverOpenTimerRef.current = null;
        if (threadPinnedRef.current) return;
        setActiveThreadId(threadId);
      }, THREAD_HOVER_OPEN_MS);
      return;
    }
    clearThreadHoverOpenTimer();
    threadHoverCloseTimerRef.current = setTimeout(() => {
      if (!threadPinnedRef.current) {
        setActiveThreadId(null);
      }
    }, 400);
  };

  useEffect(() => () => {
    clearThreadHoverCloseTimer();
    clearThreadHoverOpenTimer();
  }, []);

  // Notify parent component about thread state changes
  useEffect(() => {
    if (onThreadStateChange) {
      onThreadStateChange(!!activeThreadId);
    }
    if (!activeThreadId) {
      threadPinnedRef.current = false;
      setThreadPinned(false);
      setReplyingToMessage(null);
    }
  }, [activeThreadId, onThreadStateChange]);

  useEffect(() => {
    if (channel?.id) {
      dispatch(
        fetchSavedMessagesByChannel({
          channelId: channel.id,
          page: 0,
          size: 20,
          append: false,
        })
      );
    }
  }, [channel?.id, dispatch]);

  /** Jump to latest message (no animation). Safer than smooth scroll when layout is still settling. */
  const snapMessagesToBottom = () => {
    const c = messagesContainerRef.current;
    if (!c) return;
    c.scrollTop = c.scrollHeight;
  };

  const scheduleSnapToBottomAfterLayout = () => {
    if (isLoadingForNavigationRef.current) return;
    if (pendingNavigationMessageIdRef.current || pendingScrollMessageIdRef.current) return;
    const run = () => {
      if (isLoadingForNavigationRef.current) return;
      snapMessagesToBottom();
      showScrollToBottomRef.current = false;
      setShowScrollToBottom(false);
      isUserScrollingRef.current = false;
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    setTimeout(run, 40);
  };

  // Separate refs for thread scrolling
  const threadContainerRef = useRef(null);
  const threadEndRef = useRef(null);

  // When date-range search is active for this channel, show search results; otherwise normal messages
  const isDateRangeActive = dateRangeSearch?.channelId === channel.id && dateRangeSearch?.messages != null;
  const channelIsAttendance = checkIsAttendanceChannel(channel);
  const rawChannelMessages = React.useMemo(() => {
    const list = isDateRangeActive
      ? (dateRangeSearch.messages || [])
      : (messages[channel.id] || []);
    if (channelIsAttendance && user?.id) {
      return list.filter((m) => isMyAttendanceTimelineMessage(m, user.id));
    }
    return list;
  }, [isDateRangeActive, dateRangeSearch?.messages, messages, channel.id, channelIsAttendance, user?.id]);

  const [showChannelNotifications, setShowChannelNotifications] = useState(() =>
    isChannelNotificationsVisible(channel?.id)
  );

  useEffect(() => {
    setShowChannelNotifications(isChannelNotificationsVisible(channel?.id));
    const sync = (event) => {
      const changedId = event?.detail?.channelId;
      if (changedId && String(changedId) !== String(channel?.id)) return;
      setShowChannelNotifications(isChannelNotificationsVisible(channel?.id));
    };
    window.addEventListener(CHANNEL_NOTIFICATION_VISIBILITY_EVENT, sync);
    return () => window.removeEventListener(CHANNEL_NOTIFICATION_VISIBILITY_EVENT, sync);
  }, [channel?.id]);

  // Main timeline: top-level messages plus thread replies (with a Reply to hint on those bubbles)
  const displayMessages = React.useMemo(() => {
    const visible = rawChannelMessages.filter((m) => {
      if (m.hideFromChat) return false;
      const hidden = m.hiddenFromUserIds;
      if (user?.id && Array.isArray(hidden) && hidden.some((id) => String(id) === String(user.id))) {
        return false;
      }
      return true;
    });
    if (showChannelNotifications) return visible;
    return visible.filter((m) => !m.notificationInfo);
  }, [rawChannelMessages, showChannelNotifications, user?.id]);

  const channelSearchUsers = React.useMemo(() => {
    const memberIds = channel?.memberIds || [];
    return memberIds
      .map((memberId) => {
        const member = allUsers.find((u) => u.id === memberId);
        if (!member) return null;
        const name = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
        return {
          id: member.id,
          label: name || member.username || 'Unknown user',
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [channel?.memberIds, allUsers]);

  const assignableUsers = React.useMemo(() => {
    const memberIds = channel?.memberIds || [];
    const isDm = channel?.type === 'direct' || channel?.type === 'DIRECT_MESSAGE';
    const mapped = memberIds
      .map((memberId) => {
        const member = allUsers.find((u) => u.id === memberId);
        if (!member || member.isActive === false) return null;
        const name = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
        const baseLabel = name || member.username || 'Unknown user';
        const isSelf = user?.id && member.id === user.id;
        return {
          id: member.id,
          label: isSelf ? `You (${baseLabel})` : baseLabel,
          isSelf: Boolean(isSelf),
          profilePicture: member.profilePicture || '',
        };
      })
      .filter(Boolean);

    // DM: other person first (default assignee), then You — easier to switch
    if (isDm) {
      return mapped.sort((a, b) => {
        if (a.isSelf !== b.isSelf) return a.isSelf ? 1 : -1;
        return a.label.localeCompare(b.label);
      });
    }

    return mapped.sort((a, b) => {
      if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
  }, [channel?.memberIds, channel?.type, allUsers, user?.id]);

  const chatSearchOptions = React.useMemo(() => ({
    query: inChatSearchQuery,
    mentionsOnly: inChatMentionsOnly,
    fromUserId: inChatSearchFromUserId || null,
    userId: user?.id,
    username: user?.username,
  }), [inChatSearchQuery, inChatMentionsOnly, inChatSearchFromUserId, user?.id, user?.username]);

  const isInChatSearchActive = showInChatSearch && (
    inChatSearchQuery.trim() || inChatMentionsOnly || inChatSearchFromUserId
  );

  const searchMatchIds = React.useMemo(() => {
    if (!isInChatSearchActive) return [];
    return getChatSearchMatchIds(displayMessages, chatSearchOptions);
  }, [displayMessages, chatSearchOptions, isInChatSearchActive]);

  const messagesForList = React.useMemo(() => {
    if (!isInChatSearchActive) {
      return displayMessages;
    }
    const shouldFilterList = inChatMentionsOnly || inChatSearchFromUserId;
    if (!shouldFilterList) {
      return displayMessages;
    }
    return filterMessagesForChatSearch(displayMessages, chatSearchOptions);
  }, [displayMessages, chatSearchOptions, isInChatSearchActive, inChatMentionsOnly, inChatSearchFromUserId]);

  messagesForListRef.current = messagesForList;

  const activeSearchMatchMessageId = searchMatchIds[activeSearchMatchIndex] ?? null;
  const searchHighlightQuery = showInChatSearch && inChatSearchQuery.trim() ? inChatSearchQuery : '';

  useEffect(() => {
    setShowInChatSearch(false);
    setInChatSearchQuery('');
    setInChatMentionsOnly(false);
    setInChatSearchFromUserId('');
    setActiveSearchMatchIndex(0);
  }, [channel?.id]);

  useEffect(() => {
    setActiveSearchMatchIndex(0);
  }, [inChatSearchQuery, inChatMentionsOnly, inChatSearchFromUserId]);

  useEffect(() => {
    if (!activeSearchMatchMessageId) return;
    if (skipNextSearchScrollRef.current) return;
    const el = messageRefs.current[activeSearchMatchMessageId];
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeSearchMatchIndex, activeSearchMatchMessageId]);

  useEffect(() => {
    const restoreId = pendingSearchScrollRestoreRef.current;
    if (!restoreId) return;

    pendingSearchScrollRestoreRef.current = null;
    skipNextSearchScrollRef.current = false;

    const restoreScroll = () => {
      const el = messageRefs.current[restoreId];
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 150);
    });
  }, [messagesForList, inChatSearchQuery, inChatMentionsOnly, inChatSearchFromUserId]);

  const handleNextSearchMatch = useCallback(() => {
    if (!searchMatchIds.length) return;
    setActiveSearchMatchIndex((prev) => (prev + 1) % searchMatchIds.length);
  }, [searchMatchIds.length]);

  const handlePrevSearchMatch = useCallback(() => {
    if (!searchMatchIds.length) return;
    setActiveSearchMatchIndex((prev) => (prev - 1 + searchMatchIds.length) % searchMatchIds.length);
  }, [searchMatchIds.length]);

  const handleClearInChatSearchFilters = useCallback(() => {
    const focusedMessageId = searchMatchIds[activeSearchMatchIndex] ?? null;
    if (focusedMessageId) {
      pendingSearchScrollRestoreRef.current = focusedMessageId;
      skipNextSearchScrollRef.current = true;
    }

    setShowInChatSearch(false);
    setInChatSearchQuery('');
    setInChatMentionsOnly(false);
    setInChatSearchFromUserId('');
    setActiveSearchMatchIndex(0);
  }, [searchMatchIds, activeSearchMatchIndex]);

  const handleCloseInChatSearch = useCallback(() => {
    setShowInChatSearch(false);
    setInChatSearchQuery('');
    setInChatMentionsOnly(false);
    setInChatSearchFromUserId('');
    setActiveSearchMatchIndex(0);
  }, []);

  const handleDateRangeSearch = useCallback(async ({ startDate, endDate, userId }) => {
    if (!channel?.id || !startDate || !endDate) {
      dispatch(addNotification({
        type: 'error',
        title: 'Validation',
        message: 'Please select start and end dates.',
      }));
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      dispatch(addNotification({
        type: 'error',
        title: 'Validation',
        message: 'Please select valid dates.',
      }));
      return;
    }
    if (end < start) {
      dispatch(addNotification({
        type: 'error',
        title: 'Validation',
        message: 'End date must be on or after start date.',
      }));
      return;
    }
    const startISO = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0).toISOString();
    const endISO = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).toISOString();
    const result = await dispatch(
      fetchMessagesByDateRange({
        channelId: channel.id,
        startDate: startISO,
        endDate: endISO,
        userId: userId && String(userId).trim() !== '' ? userId : null,
      })
    );
    if (fetchMessagesByDateRange.fulfilled.match(result)) {
      setShowInChatSearch(false);
      dispatch(addNotification({
        type: 'success',
        title: 'Search complete',
        message: `Found ${result.payload.messages.length} message(s).`,
      }));
    } else if (fetchMessagesByDateRange.rejected.match(result)) {
      const errMsg = typeof result.payload === 'string'
        ? result.payload
        : result.payload?.error || result.payload?.message || 'Search failed.';
      dispatch(addNotification({
        type: 'error',
        title: 'Search failed',
        message: errMsg,
      }));
    }
  }, [channel?.id, dispatch]);

  const activeChannelMeeting = React.useMemo(
    () => findActiveChannelMeeting(rawChannelMessages),
    [rawChannelMessages],
  );

  const activeCallNoteMessages = React.useMemo(
    () => getMeetingNoteMessages(rawChannelMessages, activeChannelMeeting?.id),
    [rawChannelMessages, activeChannelMeeting?.id],
  );

  const isCallOrganizer = React.useMemo(
    () => isMeetingOrganizer(activeChannelMeeting?.meetingInfo, user?.id),
    [activeChannelMeeting, user?.id],
  );

  const handleMeetingMessageUpdated = useCallback((message) => {
    if (!message?.id) return;
    dispatch(addMessage({
      channelId: channel.id,
      message,
      currentUserId: user?.id,
      skipUnreadCountIncrement: true,
    }));
  }, [channel.id, dispatch, user?.id]);

  const handleEditEventFromTimeline = useCallback(async (eventId) => {
    if (!eventId) return;
    try {
      const event = await fetchEvent(eventId);
      setEditingEvent(event);
      setShowEventWizard(true);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.response?.data?.error || 'Failed to load event',
      }));
    }
  }, [dispatch]);

  const handleCloneEventFromTimeline = useCallback(async (eventId) => {
    if (!eventId || cloningEventId) return;
    setCloningEventId(eventId);
    try {
      const cloned = await cloneEvent(eventId);
      dispatch(addNotification({
        type: 'success',
        message: `"${cloned.name}" saved as draft`,
      }));
      setEditingEvent(cloned);
      setShowEventWizard(true);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.response?.data?.error || 'Failed to clone event',
      }));
    } finally {
      setCloningEventId(null);
    }
  }, [cloningEventId, dispatch]);

  const handleCloseCall = useCallback(async () => {
    if (!activeChannelMeeting?.id || closingCall) return;
    setClosingCall(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.patch(
        `${API_URL}/messages/${activeChannelMeeting.id}/close-meeting`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      handleMeetingMessageUpdated(data);
      setShowCloseCall(false);
      setExpandCallNotesMeetingId(null);
    } catch (err) {
      if (isAlreadyClosedError(err)) {
        handleMeetingMessageUpdated(buildClosedMeetingMessage(activeChannelMeeting, user?.id));
        setShowCloseCall(false);
        setExpandCallNotesMeetingId(null);
        return;
      }
      const msg = err.response?.data;
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg.replace(/^Error:\s*/i, '') : 'Failed to close call',
      }));
    } finally {
      setClosingCall(false);
    }
  }, [activeChannelMeeting, closingCall, dispatch, handleMeetingMessageUpdated, user?.id]);

  rawChannelMessagesRef.current = rawChannelMessages;

  const getCurrentChannelMessages = useCallback(() => {
    const state = store.getState();
    const dateRangeActive = state.chat.dateRangeSearch?.channelId === channel.id
      && state.chat.dateRangeSearch?.messages != null;
    const msgs = dateRangeActive
      ? (state.chat.dateRangeSearch.messages || [])
      : (state.chat.messages?.[channel.id] || []);
    rawChannelMessagesRef.current = msgs;
    return msgs;
  }, [channel.id]);

  // Oldest loaded message (full list, including thread replies) for pagination cursor
  const oldestLoadedMessage = React.useMemo(() => {
    if (!rawChannelMessages.length) return null;
    return [...rawChannelMessages].sort((a, b) => {
      const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
      const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
      return timeA - timeB;
    })[0];
  }, [rawChannelMessages]);

  // Filter messages for thread view (parent + replies recursively)
  const threadMessages = React.useMemo(() => {
    if (!activeThreadId) return [];
    const threadMessageIds = new Set([activeThreadId]);
    let addedNew;
    do {
      addedNew = false;
      for (const m of rawChannelMessages) {
        if (m.replyToMessageId && threadMessageIds.has(m.replyToMessageId) && !threadMessageIds.has(m.id)) {
          threadMessageIds.add(m.id);
          addedNew = true;
        }
      }
    } while (addedNew);
    return rawChannelMessages.filter(m => threadMessageIds.has(m.id));
  }, [rawChannelMessages, activeThreadId]);

  const activeThreadParentMessage = React.useMemo(() => {
    if (!activeThreadId) return null;
    return rawChannelMessages.find(m => m.id === activeThreadId) || null;
  }, [rawChannelMessages, activeThreadId]);

  const threadRepliesCount = React.useMemo(() => {
    if (!activeThreadId || threadMessages.length === 0) return 0;
    return threadMessages.filter(m => m.id !== activeThreadId).length;
  }, [threadMessages, activeThreadId]);

  const threadParticipantsCount = React.useMemo(() => {
    if (!activeThreadId || threadMessages.length <= 1) return 0;
    const replies = threadMessages.filter(m => m.id !== activeThreadId);
    const seen = new Set();
    for (const r of replies) {
      const identifier = r.senderId || r.senderName || 'unknown';
      seen.add(identifier);
    }
    return seen.size;
  }, [threadMessages, activeThreadId]);

  const isDirectMessage = isDirectMessageChannelType(channel);
  const isAnnouncementChannel = channel.type === 'ANNOUNCEMENT';
  const isAttendanceChannel = checkIsAttendanceChannel(channel);
  const isNotificationsChannel = checkIsNotificationsChannel(channel);
  const attendanceHiddenRightPanels = new Set(['savedMessages', 'files', 'managementMetrics', 'events']);
  const resolvedRightPanel = isAttendanceChannel && attendanceHiddenRightPanels.has(activeRightPanel)
    ? null
    : activeRightPanel;
  const isWorkloopChannel =
    channel.systemChannelKey === 'WORKLOOP' ||
    channel.name === 'Workloop' ||
    (channel.readOnly === true && !isAttendanceChannel && !isAnnouncementChannel && !isNotificationsChannel);
  const workloopLabel = workloopInboxLabel(user, channel);
  const isReadOnlyChannel = isAnnouncementChannel || isWorkloopChannel || isAttendanceChannel || isNotificationsChannel;
  const canViewProfile = isDirectMessage && otherUser && !isWorkloopChannel;
  const canEditChannel = !isDirectMessage && !isAttendanceChannel && !isNotificationsChannel && (
    String(channel.createdBy) === String(user?.id)
    || (Array.isArray(channel.adminIds) && channel.adminIds.some((id) => String(id) === String(user?.id)))
  );
  const hasMenuOptions = canViewProfile || canEditChannel || (!isReadOnlyChannel);
  const canUseCalendar = !isReadOnlyChannel;

  const chatStartLabel = React.useMemo(() => {
    if (isDateRangeActive) return null;
    if (hasMoreMessages) return null;
    if (isAttendanceChannel || isNotificationsChannel || isWorkloopChannel) return null;

    const firstNameOf = (person) => {
      if (!person) return '';
      const first = String(person.firstName || '').trim();
      if (first) return first;
      const full = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
      if (full) return full.split(/\s+/)[0];
      return String(person.username || person.name || '').trim();
    };

    if (isDirectMessage) {
      if (otherUser?.id && user?.id && String(otherUser.id) === String(user.id)) {
        return 'You started this chat';
      }
      const name = firstNameOf(otherUser) || firstNameOf(user) || 'Someone';
      return `${name} joined this chat`;
    }

    const creator = (allUsers || []).find((u) => String(u.id) === String(channel?.createdBy));
    const creatorName = firstNameOf(creator);
    if (creatorName) return `${creatorName} created this channel`;
    return 'Start of this channel';
  }, [
    isDateRangeActive,
    hasMoreMessages,
    isAttendanceChannel,
    isNotificationsChannel,
    isWorkloopChannel,
    isDirectMessage,
    otherUser,
    user,
    allUsers,
    channel?.createdBy,
  ]);

  useEffect(() => {
    if (!isAttendanceChannel) {
      setHasTeamAttendanceShares(false);
      return undefined;
    }

    let cancelled = false;
    const loadShares = async () => {
      try {
        const shares = await fetchSharedWithMe();
        if (cancelled) return;
        const hasShares = Array.isArray(shares) && shares.length > 0;
        setHasTeamAttendanceShares(hasShares);
        if (!hasShares) {
          setActiveRightPanel((panel) => (panel === 'teamAttendance' ? null : panel));
        }
      } catch {
        if (!cancelled) {
          setHasTeamAttendanceShares(false);
          setActiveRightPanel((panel) => (panel === 'teamAttendance' ? null : panel));
        }
      }
    };
    loadShares();
    return () => { cancelled = true; };
  }, [isAttendanceChannel, channel?.id]);

  useEffect(() => {
    const openTeamAttendance = () => setActiveRightPanel('teamAttendance');
    window.addEventListener('workloop:open-team-attendance', openTeamAttendance);
    return () => window.removeEventListener('workloop:open-team-attendance', openTeamAttendance);
  }, []);

  // Show Activity in DM when the other user shared attendance/status with me
  useEffect(() => {
    if (!isDirectMessage || isWorkloopChannel || !otherUser?.id || otherUser.id === user?.id) {
      setDmAttendanceShared(false);
      return undefined;
    }

    let cancelled = false;
    const loadDmShare = async () => {
      try {
        const shares = await fetchSharedWithMe();
        if (cancelled) return;
        const shared = Array.isArray(shares)
          && shares.some((share) => share.ownerUserId === otherUser.id);
        setDmAttendanceShared(shared);
      } catch {
        if (!cancelled) setDmAttendanceShared(false);
      }
    };
    loadDmShare();
    return () => { cancelled = true; };
  }, [isDirectMessage, isWorkloopChannel, otherUser?.id, user?.id, channel?.id]);

  useEffect(() => {
    setDmDayStatus(null);
    setDmDayTimeline(null);
  }, [channel?.id]);

  const openDmUserWorkload = () => {
    if (!otherUser?.id) return;
    setDmDayStatus(null);
    setDmDayTimeline(null);
    closeThreadPanel();
    setActiveRightPanel(null);
    if (typeof setCalendarOpen === 'function') setCalendarOpen(false);
    if (typeof onOpenUserWorkload === 'function') {
      onOpenUserWorkload(otherUser.id);
    }
    if (typeof setDailyTasksOpen === 'function') setDailyTasksOpen(true);
  };

  const openDmDayTimeline = async (date) => {
    if (!otherUser?.id || openingDmTimeline) return;
    setOpeningDmTimeline(true);
    try {
      const data = await fetchDayActivityTimeline(date || undefined, otherUser.id);
      setDmDayTimeline(data);
      setDmDayStatus(null);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.response?.data?.error || err?.message || 'Failed to open day timeline',
      }));
    } finally {
      setOpeningDmTimeline(false);
    }
  };

  // Close menu when clicking outside
  const INITIAL_PAGE_SIZE = 30;
  const countChatBubbles = (list = []) => list.filter((m) => !m.notificationInfo).length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    // Fetch messages when channel changes - always fetch latest to ensure we have all messages
    if (channel.id) {
      // Reset thread and right panel views when switching channels
      setActiveThreadId(null);
      setActiveRightPanel(null);

      // Clear the marked as read ref for the new channel
      markedAsReadRef.current = new Set();
      aroundContextRef.current = { active: false, hasMoreBefore: false, hasMoreAfter: false };
      aroundLoadInProgressRef.current = false;
      pendingScrollMessageIdRef.current = null;
      setHasMoreMessages(true);
      isLoadingOlderRef.current = false;
      olderLoadFailedRef.current = false;
      isUserScrollingRef.current = false;
      olderPageRef.current = 1;
      duplicatePageStreakRef.current = 0;
      showScrollToBottomRef.current = false;
      setShowScrollToBottom(false);

      const loadAroundContext = (messageId) => {
        if (!messageId || aroundLoadInProgressRef.current) {
          return Promise.resolve(false);
        }
        aroundLoadInProgressRef.current = true;
        return dispatch(fetchMessagesAroundTarget({
          channelId: channel.id,
          messageId,
          before: 40,
          after: 15,
        })).then((result) => {
          aroundLoadInProgressRef.current = false;
          if (result.type === 'chat/fetchMessagesAroundTarget/fulfilled') {
            aroundContextRef.current = {
              active: true,
              hasMoreBefore: result.payload.hasMoreBefore,
              hasMoreAfter: result.payload.hasMoreAfter,
            };
            setHasMoreMessages(result.payload.hasMoreBefore);
            olderPageRef.current = 1;
            duplicatePageStreakRef.current = 0;
            setShowScrollToBottom(result.payload.hasMoreAfter);
            showScrollToBottomRef.current = Boolean(result.payload.hasMoreAfter);
            showScrollToBottomRef.current = Boolean(result.payload.hasMoreAfter);
            dispatch(markChannelAsRead(channel.id));
            syncUnreadCountsFromBackend();
            pendingScrollMessageIdRef.current = messageId;
            return true;
          }
          return false;
        }).catch(() => {
          aroundLoadInProgressRef.current = false;
          return false;
        });
      };

      if (messageIdToScroll) {
        loadAroundContext(messageIdToScroll).then((ok) => {
          if (!ok) {
            dispatch(fetchMessages({ channelId: channel.id, page: 0, size: INITIAL_PAGE_SIZE })).then((result) => {
              if (result.type === 'chat/fetchMessages/fulfilled') {
                dispatch(markChannelAsRead(channel.id));
                scheduleSnapToBottomAfterLayout();
                if (countChatBubbles(result.payload?.messages) < INITIAL_PAGE_SIZE) {
                  setHasMoreMessages(false);
                }
                pendingScrollMessageIdRef.current = messageIdToScroll;
              }
            });
          }
        });
        return;
      }
      
      scheduleSnapToBottomAfterLayout();
      dispatch(fetchMessages({ channelId: channel.id, page: 0, size: INITIAL_PAGE_SIZE })).then((result) => {
        if (result.type === 'chat/fetchMessages/fulfilled') {
          dispatch(markChannelAsRead(channel.id));
          scheduleSnapToBottomAfterLayout();

          if (countChatBubbles(result.payload?.messages) < INITIAL_PAGE_SIZE) {
            setHasMoreMessages(false);
          }
        }
      });
    }
  }, [channel.id, dispatch]);

  useEffect(() => {
    // Fetch other user details for direct messages
    const fetchOtherUser = async () => {
      if (isWorkloopChannel) {
        setOtherUser(null);
        return;
      }
      if (isDirectMessage && user?.id) {
        const matched = findUserForDm(channel, user.id, allUsers);
        if (matched) {
          setOtherUser(matched);
          return;
        }
        const otherUserId = otherParticipantId(channel, user.id);
        if (otherUserId && otherUserId !== String(user.id)) {
          const resolved = await resolveUsersByIds([otherUserId], allUsers);
          if (resolved[0]) {
            setOtherUser(resolved[0]);
            return;
          }
        }
        if (otherUserId && otherUserId === String(user.id)) {
          setOtherUser(user);
          return;
        }
        setOtherUser(null);
      } else {
        setOtherUser(null);
      }
    };

    fetchOtherUser();
  }, [channel, user, isDirectMessage, isWorkloopChannel, allUsers]);

  // Sync live status updates from Redux userSlice into local otherUser state
  useEffect(() => {
    if (!otherUser?.id || allUsers.length === 0) return;
    const updated = allUsers.find((u) => extractEntityId(u) === extractEntityId(otherUser));
    if (updated && (
      updated.status !== otherUser.status
      || updated.statusMessage !== otherUser.statusMessage
      || updated.statusSource !== otherUser.statusSource
      || updated.onBreak !== otherUser.onBreak
    )) {
      setOtherUser((prev) => ({
        ...prev,
        status: updated.status,
        statusMessage: updated.statusMessage,
        statusSource: updated.statusSource,
        onBreak: updated.onBreak,
        profilePicture: updated.profilePicture || prev.profilePicture,
      }));
    }
  }, [allUsers]);

  useEffect(() => {
    if (!channel.id || !user?.username) {
      console.log('⚠️ Cannot setup WebSocket: missing channel.id or user.username');
      return;
    }

    let channelCallback = null;
    let isCleanedUp = false;

    // Subscribe even if WS is still connecting — service queues until ready.
    const setupWebSocket = () => {
      if (isCleanedUp) return;

      console.log(`📡 Setting up WebSocket subscription for channel: ${channel.id}`, {
        connected: websocketService.isConnected(),
      });

      if (websocketService.isConnected()) {
        websocketService.joinChannel(channel.id, user.username);
      }

      if (channelCallback) {
        websocketService.unsubscribeFromChannel(channel.id, channelCallback);
        channelCallback = null;
      }

      // Subscribe to channel messages
      channelCallback = (data) => {
        if (isCleanedUp) return;
        
        console.log('📨 Received WebSocket message for channel', channel.id);
        console.log('📨 Full message data:', JSON.stringify(data, null, 2));

        if (data.type === 'MESSAGE_REACTION') {
          // UI updates via MessageDto broadcast; notifications handled by ChatApp
          return;
        }
        
        // Check if this is a MessageDto (from REST API broadcast) or a simple message
        if (data.id && data.senderId && data.channelId) {
          // Handle isRead / isDelivered — Jackson may use isRead or read
          const delivery = parseDeliveryFlagsFromPayload(data);
          
          // This is a MessageDto from REST API - convert to message format
          const message = {
            id: data.id,
            content: data.content || '',
            ...pickSensitiveMessageFields(data),
            ...pickChatTranslationFields(data),
            senderId: data.senderId,
            senderName: data.senderName,
            senderProfilePicture: data.senderProfilePicture || null, // Include profile picture
            channelId: data.channelId,
            type: data.type,
            attachments: data.attachments || [],
            attachmentGroupName: data.attachmentGroupName || null,
            resourceGroupId: data.resourceGroupId || null,
            reactions: data.reactions || [],
            customTags: data.customTags || [],
            replyToMessageId: data.replyToMessageId || null, // Include replyToMessageId
            mentionedUserIds: data.mentionedUserIds || [],
            ...delivery,
            isEdited: data.isEdited || false,
            linkPreview: data.linkPreview || null, // Include linkPreview
            meetingInfo: data.meetingInfo || null,
            appointmentInfo: data.appointmentInfo || null,
            eventInfo: data.eventInfo || null,
            attendanceInfo: data.attendanceInfo || null,
            notificationInfo: data.notificationInfo || null,
            workItemShare: data.workItemShare || null,
            linkedMeetingMessageId: data.linkedMeetingMessageId || null,
            workflowAssignedToUserId: data.workflowAssignedToUserId ?? null,
            workflowAssignedToName: data.workflowAssignedToName ?? null,
            workflowDescription: data.workflowDescription ?? null,
            workflowTitle: data.workflowTitle ?? null,
            workflowSpaceId: data.workflowSpaceId ?? null,
            workflowSpaceName: data.workflowSpaceName ?? null,
            workflowTrackId: data.workflowTrackId ?? null,
            workflowTrackLink: data.workflowTrackLink ?? null,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            timestamp: data.timestamp || data.createdAt,
          };
          
          // Only process if it's for the current channel
          if (String(message.channelId) === String(channel.id)) {
            const isOwnAttendanceEntry = !channelIsAttendance
              || isMyAttendanceTimelineMessage(message, user?.id);
            if (!isOwnAttendanceEntry) {
              return;
            }
            console.log('✅ Channel message received:', {
              id: message.id,
              from: message.senderName,
              content: message.content?.substring(0, 50),
              isDirectMessage: isDirectMessage
            });

            const state = store.getState();
            const existingMessages = state.chat.messages[channel.id] || [];
            const existingMessage = existingMessages.find((m) => m.id === message.id);
            const ownReactionBroadcast = isOwnReactionBroadcast(existingMessage, message, user?.id);

            // Always dispatch addMessage in ChatWindow as well - addMessage handles deduping
            // This ensures the message reflects even if the global ChatApp subscription has issues
            dispatch(addMessage({ 
              channelId: channel.id, 
              message: message, 
              currentUserId: user?.id,
              skipUnreadCountIncrement: ownReactionBroadcast,
            }));

            if (message.senderId !== user?.id) {
              acknowledgeMessageDelivered(message, user?.id);
            }

            if (shouldAnnounceIncomingMessage({
              existingMessage,
              incoming: message,
              currentUserId: user?.id,
              currentChannelId: channel.id,
              tabVisible: notificationService.isTabVisibleNow(),
              windowFocused: notificationService.isWindowFocusedNow(),
            })) {
              const channelName = isDirectMessage && otherUser
                ? `${otherUser.firstName} ${otherUser.lastName}`
                : channel.name || 'Channel';
              notificationService.showMessageNotification(message, channelName, isDirectMessage, channel.id, user);
            }

            // Mark channel as read when message arrives (user is viewing it)
            dispatch(markChannelAsRead(channel.id));
            syncUnreadCountsFromBackend();
          } else {
            console.log('⚠️ Message for different channel:', message.channelId, 'vs', channel.id);
          }
        } else if (data.type === 'MESSAGE') {
          // Legacy WS payload (no message id) — ChatApp only dispatches when data.id is set
          if (data.channelId === channel.id) {
            const isOwnAttendanceEntry = !channelIsAttendance
              || isMyAttendanceTimelineMessage(data, user?.id);
            if (!isOwnAttendanceEntry) {
              return;
            }
            console.log('✅ Adding simple WebSocket message to chat');
            dispatch(addMessage({ channelId: channel.id, message: data, currentUserId: user?.id }));

            if (shouldAnnounceIncomingMessage({
              existingMessage: null,
              incoming: data,
              currentUserId: user?.id,
              currentChannelId: channel.id,
              tabVisible: notificationService.isTabVisibleNow(),
              windowFocused: notificationService.isWindowFocusedNow(),
            })) {
              const channelName = isDirectMessage && otherUser
                ? `${otherUser.firstName} ${otherUser.lastName}`
                : channel.name || 'Channel';
              notificationService.showMessageNotification(data, channelName, isDirectMessage, channel.id, user);
            }

            dispatch(markChannelAsRead(channel.id));
            syncUnreadCountsFromBackend();
          }
        } else if (data.type === 'TYPING') {
          // Handle typing status - channelId is now included in the payload
          const typingChannelId = data.channelId || channel.id;
          console.log('📝 Received typing status:', {
            typingChannelId,
            currentChannelId: channel.id,
            username: data.username,
            isTyping: data.isTyping,
            matches: typingChannelId === channel.id
          });
          
          if (typingChannelId === channel.id) {
            dispatch(setTypingUser({ 
              channelId: channel.id, 
              username: data.username, 
              isTyping: data.isTyping 
            }));
          }
        } else if (data.type === 'USER_JOINED') {
          // User joined notification removed - no popup needed
        } else if (data.type === 'MESSAGE_DELETED') {
          // Handle message deletion
          if (data.channelId === channel.id && data.messageId) {
            // Remove message from Redux store
            dispatch(removeMessage({ channelId: channel.id, messageId: data.messageId }));
          }
        } else {
          console.log('⚠️ Unknown message type or format:', data);
        }
      };

      websocketService.subscribeToChannel(channel.id, channelCallback);
      websocketService.retryPendingSubscriptions();
      console.log(`✅ Channel subscription registered: ${channel.id}`);
    };

    setupWebSocket();

    const onWsReady = () => {
      if (isCleanedUp) return;
      setupWebSocket();
      if (user?.username) {
        websocketService.joinChannel(channel.id, user.username);
      }
    };
    window.addEventListener('workloop:ws-connected', onWsReady);
    window.addEventListener('workloop:ws-reconnected', onWsReady);

    return () => {
      isCleanedUp = true;
      window.removeEventListener('workloop:ws-connected', onWsReady);
      window.removeEventListener('workloop:ws-reconnected', onWsReady);
      console.log(`🔌 Cleaning up WebSocket subscription callback for channel: ${channel.id}`);
      if (channelCallback) {
        websocketService.unsubscribeFromChannel(channel.id, channelCallback);
        channelCallback = null;
      }
    };
  }, [channel.id, user?.username, dispatch]);

  useEffect(() => {
    // Same channel only: opening a channel is handled after fetch (instant snap).
    if (isLoadingForNavigationRef.current) return;
    if (pendingNavigationMessageIdRef.current || pendingScrollMessageIdRef.current) return;
    if (messageIdToScroll || threadIdToOpen) return;

    const messagesContainer = messagesContainerRef.current;
    const endEl = messagesEndRef.current;
    if (!endEl || !messagesContainer) return;

    const prevId = prevChannelIdForAutoScrollRef.current;
    const switchedChannel = prevId !== channel.id;
    prevChannelIdForAutoScrollRef.current = channel.id;
    if (switchedChannel) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
    const forceToLatest = pendingStickToBottomRef.current;

    if (forceToLatest || (isNearBottom && !isUserScrollingRef.current)) {
      pendingStickToBottomRef.current = true;
      scheduleSnapToBottomAfterLayout();
      setTimeout(() => {
        if (document.visibilityState === 'hidden') return;
        if (typeof document.hasFocus === 'function' && !document.hasFocus()) return;
        pendingStickToBottomRef.current = false;
      }, 600);
    }
  }, [displayMessages.length, channel.id, messageIdToScroll, threadIdToOpen]);

  useEffect(() => {
    // Scroll to bottom of thread when new thread messages arrive
    if (isLoadingForNavigationRef.current) return;
    if (pendingNavigationMessageIdRef.current) return;

    if (threadContainerRef.current && threadEndRef.current) {
      const container = threadContainerRef.current;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
      
      // Auto-scroll if near bottom or if we just opened the thread
      if (isNearBottom || threadMessages.length <= 15) {
        setTimeout(() => {
          threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [threadMessages.length, activeThreadId]);

  const stickMainTimelineToLatest = () => {
    pendingStickToBottomRef.current = true;
    isUserScrollingRef.current = false;
    pendingNavigationMessageIdRef.current = null;
    pendingScrollMessageIdRef.current = null;
    messageNavigationGenerationRef.current += 1;
    isLoadingForNavigationRef.current = false;
    dispatch(invalidateAroundMessageFetches());

    if (dateRangeSearch?.channelId === channel.id && dateRangeSearch?.messages != null) {
      dispatch(clearDateRangeSearch());
    }

    const around = aroundContextRef.current;
    if (around?.active && around.hasMoreAfter) {
      aroundContextRef.current = { active: false, hasMoreBefore: false, hasMoreAfter: false };
      setHasMoreMessages(true);
      olderPageRef.current = 1;
      duplicatePageStreakRef.current = 0;
      dispatch(fetchMessages({ channelId: channel.id, page: 0, size: INITIAL_PAGE_SIZE })).then(() => {
        scheduleSnapToBottomAfterLayout();
      });
    }

    scheduleSnapToBottomAfterLayout();
    setTimeout(() => {
      if (document.visibilityState === 'hidden') return;
      if (typeof document.hasFocus === 'function' && !document.hasFocus()) return;
      pendingStickToBottomRef.current = false;
    }, 700);
  };
  stickMainTimelineToLatestRef.current = stickMainTimelineToLatest;

  useEffect(() => {
    const shouldSkipResumeSnap = () => (
      isLoadingForNavigationRef.current
      || Boolean(pendingNavigationMessageIdRef.current)
      || Boolean(pendingScrollMessageIdRef.current)
      || Boolean(messageIdToScroll)
      || Boolean(threadIdToOpen)
      || (dateRangeSearch?.channelId === channel.id && dateRangeSearch?.messages != null)
    );

    const markWindowAway = () => {
      windowWasAwayRef.current = true;
    };

    const snapToLatestOnReturn = () => {
      if (!windowWasAwayRef.current) return;
      windowWasAwayRef.current = false;
      if (shouldSkipResumeSnap()) return;
      // Same rule as the down-arrow: only snap to latest if that button is hidden
      // (user is already at the latest chat). If it is visible, they are reading old chat.
      if (showScrollToBottomRef.current) return;
      stickMainTimelineToLatestRef.current?.();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        markWindowAway();
        return;
      }
      snapToLatestOnReturn();
    };

    window.addEventListener('blur', markWindowAway);
    window.addEventListener('focus', snapToLatestOnReturn);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('blur', markWindowAway);
      window.removeEventListener('focus', snapToLatestOnReturn);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [channel.id, dateRangeSearch?.channelId, dateRangeSearch?.messages, messageIdToScroll, threadIdToOpen]);

  useEffect(() => {
    const pinTimelineIfFollowingLatest = () => {
      if (isLoadingForNavigationRef.current) return;
      if (isLoadingOlderRef.current) return;
      if (pendingNavigationMessageIdRef.current) return;
      if (aroundLoadInProgressRef.current) return;
      if (pendingStickToBottomRef.current || !isUserScrollingRef.current) {
        snapMessagesToBottom();
        showScrollToBottomRef.current = false;
        setShowScrollToBottom(false);
      }
    };

    const contentEl = timelineContentRef.current;
    if (!contentEl) return undefined;
    const observer = new ResizeObserver(pinTimelineIfFollowingLatest);
    observer.observe(contentEl);
    return () => observer.disconnect();
  }, [channel.id]);

  useEffect(() => {
    const handleStickToLatest = (event) => {
      const detail = event?.detail || {};
      if (detail.channelId && detail.channelId !== channel.id) return;
      const messageId = detail.messageId;
      const list = messagesForListRef.current || [];
      const last = list[list.length - 1];
      const isLatest = !messageId || Boolean(
        last && (String(last.id) === String(messageId) || String(last.tempId) === String(messageId)),
      );
      if (isLatest || detail.force === true) {
        stickMainTimelineToLatestRef.current?.();
        return;
      }
      if (messageId) {
        setTimeout(() => {
          const el = messageRefs.current?.[messageId];
          el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 80);
      }
    };
    window.addEventListener('workloop:stick-timeline-to-latest', handleStickToLatest);
    return () => window.removeEventListener('workloop:stick-timeline-to-latest', handleStickToLatest);
  }, [channel.id]);

  // Function to scroll to bottom (explicit control — instant to match channel open behavior)
  const scrollToBottom = () => {
    stickMainTimelineToLatest();
  };

  // Instant steps — smooth scroll fights key-repeat and looks like stuttering.
  const TIMELINE_SCROLL_PX = 120;
  const TIMELINE_JUMP_COUNT = 10;
  // Sticky date chip sits over the top of the timeline; align under it.
  const TIMELINE_STICKY_OFFSET_PX = 52;

  const scrollTimelineByPx = (deltaY) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    isUserScrollingRef.current = true;
    pendingStickToBottomRef.current = false;
    container.scrollTop += deltaY;
  };

  const getMessageTimelineEl = (message) => {
    if (!message) return null;
    const id = message.id || message.tempId;
    if (id == null) return null;
    return messageRefs.current?.[id] || messageRefs.current?.[String(id)] || null;
  };

  const getFirstVisibleTimelineIndex = () => {
    const container = messagesContainerRef.current;
    const list = messagesForListRef.current || [];
    if (!container || !list.length) return 0;
    const containerTop = container.getBoundingClientRect().top + TIMELINE_STICKY_OFFSET_PX;
    for (let i = 0; i < list.length; i += 1) {
      const el = getMessageTimelineEl(list[i]);
      if (!el) continue;
      if (el.getBoundingClientRect().bottom > containerTop) {
        return i;
      }
    }
    return 0;
  };

  const beginTimelineSmoothJump = () => {
    // Block older-page scroll restore from cancelling the animation mid-flight.
    timelineSmoothJumpUntilRef.current = Date.now() + 700;
  };

  const scrollTimelineMessageIntoView = (message) => {
    const el = getMessageTimelineEl(message);
    const container = messagesContainerRef.current;
    if (!el || !container) return false;
    const delta = el.getBoundingClientRect().top
      - container.getBoundingClientRect().top
      - TIMELINE_STICKY_OFFSET_PX;
    if (Math.abs(delta) < 2) return false;
    beginTimelineSmoothJump();
    container.scrollBy({ top: delta, behavior: 'smooth' });
    return true;
  };

  const scrollTimelineByPxSmooth = (deltaY) => {
    const container = messagesContainerRef.current;
    if (!container || !deltaY) return;
    isUserScrollingRef.current = true;
    pendingStickToBottomRef.current = false;
    beginTimelineSmoothJump();
    container.scrollBy({ top: deltaY, behavior: 'smooth' });
  };

  const findJumpTargetMessage = (list, preferredIndex, direction) => {
    if (!list.length) return null;
    const start = Math.max(0, Math.min(list.length - 1, preferredIndex));
    if (getMessageTimelineEl(list[start])) return list[start];
    const step = direction < 0 ? -1 : 1;
    for (let i = start + step; i >= 0 && i < list.length; i += step) {
      if (getMessageTimelineEl(list[i])) return list[i];
    }
    for (let i = start - step; i >= 0 && i < list.length; i -= step) {
      if (getMessageTimelineEl(list[i])) return list[i];
    }
    return list[start] || null;
  };

  const waitForTimelineListSettle = async (anchorId, previousLength) => {
    for (let i = 0; i < 40; i += 1) {
      if (isLoadingOlderRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 25));
        continue;
      }
      const list = messagesForListRef.current || [];
      if (list.length > previousLength) {
        if (!anchorId) return list;
        const idx = list.findIndex((m) => String(m.id || m.tempId) === String(anchorId));
        if (idx >= 0) return list;
      }
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
    return messagesForListRef.current || [];
  };

  const jumpTimelineUp = async () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    isUserScrollingRef.current = true;
    pendingStickToBottomRef.current = false;

    const listBefore = messagesForListRef.current || [];
    const firstVisible = getFirstVisibleTimelineIndex();
    const anchor = listBefore[firstVisible];
    const anchorId = anchor?.id || anchor?.tempId;
    const previousLength = listBefore.length;

    if (firstVisible < TIMELINE_JUMP_COUNT && hasMoreMessagesRef.current) {
      // Wait out any in-flight older-page load from near-top scroll.
      for (let i = 0; i < 40 && isLoadingOlderRef.current; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 25));
      }
      if (!isLoadingOlderRef.current) {
        await loadOlderMessagesPage({ preserveScroll: true });
      }
      // preserveScroll restores at +50ms; wait for that + React to commit new rows.
      await new Promise((resolve) => setTimeout(resolve, 80));
      await waitForTimelineListSettle(anchorId, previousLength);
    }

    const list = messagesForListRef.current || [];
    let anchorIndex = firstVisible;
    if (anchorId) {
      const found = list.findIndex((m) => String(m.id || m.tempId) === String(anchorId));
      if (found >= 0) anchorIndex = found;
    }

    const targetIndex = Math.max(0, anchorIndex - TIMELINE_JUMP_COUNT);
    const target = findJumpTargetMessage(list, targetIndex, -1);
    if (target && scrollTimelineMessageIntoView(target)) return;

    // Fallback when refs/list are stale: still move the viewport up smoothly.
    const fallback = Math.min(Math.round(container.clientHeight * 0.75), 720);
    if (container.scrollTop > 0) {
      scrollTimelineByPxSmooth(-Math.min(fallback, container.scrollTop));
    }
  };

  const jumpTimelineDown = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    isUserScrollingRef.current = true;
    pendingStickToBottomRef.current = false;

    const list = messagesForListRef.current || [];
    const firstVisible = getFirstVisibleTimelineIndex();
    const targetIndex = Math.min(list.length - 1, firstVisible + TIMELINE_JUMP_COUNT);
    const target = findJumpTargetMessage(list, targetIndex, 1);
    if (target && scrollTimelineMessageIntoView(target)) return;

    const fallback = Math.min(Math.round(container.clientHeight * 0.75), 720);
    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    const remaining = maxScroll - container.scrollTop;
    if (remaining > 0) {
      scrollTimelineByPxSmooth(Math.min(fallback, remaining));
    }
  };

  jumpTimelineUpRef.current = () => scrollTimelineByPx(-TIMELINE_SCROLL_PX);
  jumpTimelineDownRef.current = () => scrollTimelineByPx(TIMELINE_SCROLL_PX);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'ArrowUp' && shouldHandleTimelineArrowKey(event)) {
        event.preventDefault();
        jumpTimelineUpRef.current?.();
        return;
      }
      if (event.key === 'ArrowDown' && shouldHandleTimelineArrowKey(event)) {
        event.preventDefault();
        jumpTimelineDownRef.current?.();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const handleSendMessage = async (
    content,
    attachments = [],
    replyToMessageId = null,
    linkPreview = null,
    mentionedUserIds = [],
    attachmentGroupName = null,
    targetResourceGroupId = null,
    sensitiveMeta = null
  ) => {
    const isSensitive = Boolean(sensitiveMeta?.sensitive);
    if (!content.trim() && attachments.length === 0) return;

    // Only set replyToMessageId if it is explicitly passed (e.g., from the thread input).
    // Do not fall back to activeThreadId, otherwise typing in the main channel input
    // while a thread is open in the sidebar will mistakenly send the message as a reply.
    const effectiveReplyToId = replyToMessageId || null;

    // Generate temporary ID for optimistic message
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create optimistic message
    const optimisticMessage = {
      tempId,
      id: tempId, // Use tempId as id for now
      content: isSensitive ? '' : (content || ''),
      sensitive: isSensitive,
      sensitiveTitle: isSensitive ? (sensitiveMeta?.sensitiveTitle || '') : undefined,
      allowReceiverPin: isSensitive ? sensitiveMeta?.allowReceiverPin === true : undefined,
      hasSenderPin: Boolean(isSensitive && sensitiveMeta?.senderPin),
      senderId: user?.id,
      senderName: user?.username || user?.firstName || 'You',
      senderProfilePicture: user?.profilePicture || null,
      channelId: channel.id,
      type: 'MESSAGE',
      attachments: attachments || [],
      attachmentGroupName: attachmentGroupName || null,
      resourceGroupId: null,
      reactions: [],
      replyToMessageId: effectiveReplyToId,
      readBy: [],
      isRead: false,
      deliveredBy: [],
      isDelivered: false,
      isEdited: false,
      linkPreview: isSensitive ? null : (linkPreview || null),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      timestamp: new Date().toISOString(),
    };

    // Add optimistic message immediately
    dispatch(addOptimisticMessage({
      channelId: channel.id,
      message: optimisticMessage,
    }));

    if (effectiveReplyToId) {
      setTimeout(() => {
        threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    } else {
      stickMainTimelineToLatest();
    }

    try {
      console.log('📤 Sending message via REST API to channel:', channel.id, {
        replyToMessageId: effectiveReplyToId,
        hasReply: !!effectiveReplyToId,
        tempId,
      });
      // Always send via REST API for persistence (especially for attachments)
      // The REST API will broadcast via WebSocket automatically to all subscribers
      const sentMessage = await dispatch(sendMessage({
        content: content || '',
        channelId: channel.id,
        senderId: user?.id,
        replyToMessageId: effectiveReplyToId,
        mentionedUserIds: mentionedUserIds?.length > 0 ? mentionedUserIds : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        attachmentGroupName: attachmentGroupName || undefined,
        targetResourceGroupId: targetResourceGroupId || undefined,
        linkPreview: isSensitive ? undefined : (linkPreview || undefined),
        tempId, // Pass tempId to match with optimistic message
        sensitive: isSensitive || undefined,
        senderPin: isSensitive ? sensitiveMeta?.senderPin : undefined,
        allowReceiverPin: isSensitive ? sensitiveMeta?.allowReceiverPin === true : undefined,
        sensitiveTitle: isSensitive ? sensitiveMeta?.sensitiveTitle : undefined,
      })).unwrap();
      
      // Debug: Verify replyToMessageId was sent and received
      if (effectiveReplyToId) {
        console.log('✅ Reply message sent:', {
          messageId: sentMessage.id,
          replyToMessageId: sentMessage.replyToMessageId,
          expectedReplyTo: effectiveReplyToId,
          match: sentMessage.replyToMessageId === effectiveReplyToId
        });
      }
      
      // Clear inline reply state if not in thread view
      if (!activeThreadId) {
        // removed setReplyingTo
      }
      
      console.log('✅ Message sent successfully, should be broadcasted via WebSocket:', {
        id: sentMessage.id,
        channelId: sentMessage.channelId,
        content: sentMessage.content?.substring(0, 50),
        tempId,
      });
      // The optimistic message will be replaced by the server message in the reducer
      // WebSocket will also broadcast it, but we check for duplicates
    } catch (error) {
      console.error('❌ Error sending message:', error);
      console.error('❌ Error structure:', {
        error,
        status: error?.status,
        data: error?.data,
        originalError: error?.originalError,
        response: error?.response,
        message: error?.message
      });

      // Check if it's an authentication error (token expired or invalid)
      // The error from unwrap() will have the structure we set in rejectWithValue
      const status = error?.status
        || error?.originalError?.response?.status
        || error?.response?.status
        || (error?.originalError && error.originalError.response?.status);

      // Also check error message/data for auth-related strings as fallback
      const errorString = JSON.stringify(error).toLowerCase();
      const hasAuthErrorInMessage = errorString.includes('401') || errorString.includes('403')
        || errorString.includes('unauthorized') || errorString.includes('forbidden')
        || errorString.includes('token expired') || errorString.includes('invalid token');

      const isAuthError = status === 401 || status === 403 || hasAuthErrorInMessage;

      console.log('🔍 Auth check:', {
        status,
        isAuthError,
        hasAuthErrorInMessage,
        errorType: typeof error,
        errorKeys: error ? Object.keys(error) : []
      });

      if (isAuthError) {
        console.log('🔐 Authentication error detected, redirecting to login...');
        // Remove token from localStorage
        localStorage.removeItem('token');
        // Redirect to login page - draft will remain in localStorage
        // Use replace to prevent back navigation
        navigate('/login', { replace: true });
        // Don't show error notification for auth errors
        // Re-throw the error so MessageInput can handle it and preserve the message
        throw error;
      }

      // Update optimistic message status to failed
      // The reducer will handle this, but we can also update it here for immediate feedback
      dispatch(updateMessageStatus({
        channelId: channel.id,
        tempId,
        status: 'failed',
      }));

      // For other errors, show notification
      let errorMessage = 'Failed to send message. Please try again.';

      // Extract error message from the error structure
      const errorData = error?.data || error?.response?.data || error;

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      dispatch(addNotification({
        type: 'error',
        title: 'Failed to send message',
        message: errorMessage,
      }));
      // Don't re-throw - let the optimistic message stay with failed status
      // User can retry using the retry button
    }
  };

  // Retry handler for failed messages
  const handleRetryMessage = async (tempId, message) => {
    try {
      // Update status back to pending
      dispatch(updateMessageStatus({
        channelId: channel.id,
        tempId,
        status: 'pending',
      }));

      // Retry sending the message
      const sentMessage = await dispatch(sendMessage({
        content: message.content || '',
        channelId: channel.id,
        senderId: user?.id,
        replyToMessageId: message.replyToMessageId,
        attachments: message.attachments?.length > 0 ? message.attachments : undefined,
        attachmentGroupName: message.attachmentGroupName || undefined,
        tempId,
      })).unwrap();

      console.log('✅ Message retried successfully:', {
        id: sentMessage.id,
        channelId: sentMessage.channelId,
      });
    } catch (error) {
      console.error('❌ Error retrying message:', error);
      // Status will be updated to failed by the reducer
      dispatch(addNotification({
        type: 'error',
        title: 'Failed to retry message',
        message: 'Please try again.',
      }));
    }
  };

  const handleTyping = (isTyping) => {
    console.log('⌨️ handleTyping called:', { isTyping, channelId: channel.id, username: user.username });
    
    if (isTyping) {
      setIsTyping(true);
      console.log('📤 Sending typing status: true');
      websocketService.sendTypingStatus(channel.id, user.username, true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to stop typing indicator after inactivity
      typingTimeoutRef.current = setTimeout(() => {
        console.log('⏱️ Typing timeout - stopping typing indicator');
        setIsTyping(false);
        websocketService.sendTypingStatus(channel.id, user.username, false);
      }, 2000); // 2 seconds of inactivity
    } else {
      setIsTyping(false);
      console.log('📤 Sending typing status: false');
      websocketService.sendTypingStatus(channel.id, user.username, false);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  const handleAddReaction = async (messageId, emoji, options = {}) => {
    const msg = (messages[channel?.id] || []).find((item) => (item.id || item._id) === messageId);
    const resolvedEmoji = resolveUrgentWorkflowReactionId(emoji);

    // Task work-item type is handled in MessageItem (opens task dialog). Never apply as workflow.
    if (resolvedEmoji === WORK_ITEM_TYPE_TASK_ID && !options.skipCreateDialog) {
      return undefined;
    }

    if (
      typeof resolvedEmoji === 'string'
      && isWorkflowCreatingWorkItemType(resolvedEmoji)
      && messageId
      && !options.skipCreateDialog
    ) {
      const existingType = getActiveWorkflowByCategory(msg?.reactions || [])['work-item-type'];
      if (existingType === resolvedEmoji) {
        dispatch(addNotification({
          type: 'error',
          message: 'Workflow type cannot be removed once set',
        }));
        return undefined;
      }
      const hasTitle = Boolean((msg?.workflowTitle || '').trim());
      // First work-item type needs title via create dialog.
      if (!existingType && !hasTitle) {
        const chatText = stripHtmlForTextExtraction(msg?.content || '')
          .replace(/\s+/g, ' ')
          .trim();
        setCreateWorkflowDraft({
          messageId,
          typeId: resolvedEmoji,
          initialDescription: chatText,
          sourceContent: msg?.content || '',
        });
        return;
      }
    }

    // Locked: once a work-item type exists, cannot clear it or change it to Task.
    if (typeof resolvedEmoji === 'string' && resolvedEmoji.startsWith('wf:work-item-type:') && messageId) {
      const existingType = getActiveWorkflowByCategory(msg?.reactions || [])['work-item-type'];
      if (existingType) {
        if (existingType === resolvedEmoji) {
          dispatch(addNotification({
            type: 'error',
            message: 'Workflow type cannot be removed once set',
          }));
          return undefined;
        }
        if (resolvedEmoji === WORK_ITEM_TYPE_TASK_ID) {
          dispatch(addNotification({
            type: 'error',
            message: 'Cannot change a workflow to a task',
          }));
          return undefined;
        }
      }
    }

    let completionTimeInMinutes;
    if (resolvedEmoji === WORKFLOW_CLOSED_STATUS_ID && messageId && messageIsRaisedWorkflow(msg?.reactions)) {
      if (!messageIsWorkflowClosed(msg)) {
        let linkedTasks = [];
        try {
          const existing = await dispatch(fetchTasks({ messageId })).unwrap();
          linkedTasks = workflowLinkedTasksFrom(existing);
        } catch (err) {
          dispatch(addNotification({
            type: 'error',
            message: apiErrorMessage(err, 'Failed to load workflow tasks'),
          }));
          return;
        }
        const mappedByTaskId = await loadMappedByTaskId(
          linkedTasks,
          (taskId) => dispatch(fetchTasksByMapTaskId(taskId)).unwrap(),
        );
        const { minutes, error } = await resolveWorkflowCloseMinutes({
          linkedTasks,
          mappedByTaskId,
          workflowTitle: msg?.workflowTitle,
        });
        if (error) {
          dispatch(addNotification({ type: 'error', message: error }));
          return;
        }
        if (minutes == null) return;
        if (Number(minutes) > 0) {
          completionTimeInMinutes = minutes;
        }
      }
    }

    let urgentDeadlineAt;
    if (resolvedEmoji === WORKFLOW_URGENT_ID && messageId) {
      if (!messageHasUrgentWorkflow(msg?.reactions)) {
        const deadline = options.urgentDeadlineAt || await promptUrgentDeadline();
        if (!deadline) return undefined;
        urgentDeadlineAt = deadline;
      }
    }

    dispatch(addReaction({
      messageId,
      emoji: resolvedEmoji,
      channelId: channel?.id,
      userId: user?.id,
      completionTimeInMinutes,
      urgentDeadlineAt,
      ...(options.workflowTitle ? { workflowTitle: options.workflowTitle } : {}),
      ...(options.workflowDescription != null
        ? { workflowDescription: options.workflowDescription }
        : {}),
      ...(options.workflowSpaceId ? { workflowSpaceId: options.workflowSpaceId } : {}),
      ...(options.workflowSpaceName ? { workflowSpaceName: options.workflowSpaceName } : {}),
    }))
      .unwrap()
      .then((updated) => {
        if (resolvedEmoji === WORKFLOW_CLOSED_STATUS_ID && messageId && messageIsRaisedWorkflow(updated?.reactions)) {
          notifyWorkflowsAssignedChanged({
            messageId,
            action: 'completed',
            completionTimeInMinutes,
          });
        } else if (isWorkflowReaction(resolvedEmoji) && messageId && messageIsRaisedWorkflow(updated?.reactions)) {
          notifyWorkflowsAssignedChanged({ messageId });
        }

        if (isWorkflowReaction(resolvedEmoji) && messageId) {
          const list = messagesForListRef.current || [];
          const last = list[list.length - 1];
          const isLatest = Boolean(
            last && (String(last.id) === String(messageId) || String(last.tempId) === String(messageId)),
          );
          if (isLatest || pendingStickToBottomRef.current || !isUserScrollingRef.current) {
            pendingStickToBottomRef.current = true;
            scheduleSnapToBottomAfterLayout();
            setTimeout(() => {
              pendingStickToBottomRef.current = false;
            }, 700);
          } else {
            setTimeout(() => {
              const el = messageRefs.current?.[messageId];
              el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 80);
          }
        }
      })
      .catch((err) => {
        dispatch(addNotification({
          type: 'error',
          message: apiErrorMessage(err, 'Failed to update reaction'),
        }));
      });
    return undefined;
  };

  const handleCreateWorkflowFromChat = async ({ title, description, spaceId, spaceName }) => {
    if (!createWorkflowDraft?.messageId || !createWorkflowDraft?.typeId || creatingWorkflow) return;
    const nextTitle = (title || '').trim();
    if (!nextTitle) return;
    const draft = createWorkflowDraft;
    const chatPlain = (draft.initialDescription || '').trim();
    const descriptionText = (description || '').trim();
    const workflowDescription = descriptionText === chatPlain
      && draft.sourceContent
      ? draft.sourceContent
      : descriptionText;
    setCreateWorkflowDraft(null);
    setCreatingWorkflow(true);
    setTemplatePosting({ channelId: channel?.id, label: 'Creating workflow…' });
    window.setTimeout(() => scrollToBottom(), 0);
    try {
      const updated = await handleAddReaction(draft.messageId, draft.typeId, {
        skipCreateDialog: true,
        workflowTitle: nextTitle,
        workflowDescription,
        ...(spaceId || channel?.spaceId ? { workflowSpaceId: spaceId || channel.spaceId } : {}),
        ...(spaceName || channel?.spaceName ? { workflowSpaceName: spaceName || channel.spaceName } : {}),
      });
      if (!updated) return;
      dispatch(fetchTasks({}));
      dispatch(addNotification({
        type: 'success',
        message: 'Workflow created',
      }));
    } finally {
      setCreatingWorkflow(false);
      setTemplatePosting(null);
    }
  };

  const handleEditMessage = async (messageId, newContent) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/messages/${messageId}`,
        { content: newContent },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // Ensure all required fields are present in the response
      const updatedMessage = {
        ...response.data,
        isEdited: response.data.isEdited !== undefined ? response.data.isEdited : true,
        updatedAt: response.data.updatedAt || new Date().toISOString(),
        linkPreview: response.data.linkPreview || null,
      };
      
      // Update message in Redux store immediately
      dispatch(addMessage({ channelId: channel.id, message: updatedMessage, currentUserId: user?.id }));
      
      return updatedMessage;
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/messages/${messageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // Remove message from Redux store immediately
      dispatch(removeMessage({ channelId: channel.id, messageId }));
      // The WebSocket will also send a MESSAGE_DELETED event, but we've already removed it
      return true;
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  };

  // Handle forwarding a message to a selected channel
  const handleForwardMessage = async (messageToForward, targetChannel) => {
    try {
      const messageContent = messageToForward.content || '';
      const attachments = messageToForward.attachments && messageToForward.attachments.length > 0
        ? messageToForward.attachments
        : undefined;
      const eventInfo = messageToForward.eventInfo?.eventId ? messageToForward.eventInfo : undefined;

      const isDirectMessage = targetChannel.type === 'direct' || targetChannel.type === 'DIRECT_MESSAGE';
      const channelName = isDirectMessage
        ? 'Direct message'
        : (targetChannel.name || 'channel');

      await dispatch(sendMessage({
        content: messageContent,
        channelId: targetChannel.id,
        senderId: user?.id,
        attachments,
        attachmentGroupName: messageToForward.attachmentGroupName || undefined,
        eventInfo,
      })).unwrap();

      if (eventInfo?.eventId) {
        const memberIds = normalizeMemberIds(targetChannel.memberIds);
        const otherId = otherParticipantId(targetChannel, user?.id);
        const targetUserIds = [...new Set([
          ...memberIds.filter((id) => id && String(id) !== String(user?.id)),
          otherId,
        ].filter(Boolean))];
        recordEventForward(eventInfo.eventId, {
          targetChannelId: targetChannel.id,
          targetChannelName: channelName,
          targetUserIds,
        }).catch(() => {});
      }

      dispatch(setCurrentChannel(targetChannel));

      dispatch(addNotification({
        type: 'success',
        title: 'Message forwarded',
        message: `Message forwarded to ${channelName}`,
      }));
    } catch (error) {
      console.error('Error forwarding message:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Failed to forward message',
        message: error?.data || error?.message || 'Failed to forward message. Please try again.',
      }));
    }
  };

  // Handle scroll to load older messages and show/hide scroll to bottom button
  const loadOlderMessagesPage = useCallback(async ({ preserveScroll = true } = {}) => {
    if (isLoadingOlderRef.current) {
      return { success: false, reason: 'busy' };
    }
    if (!hasMoreMessagesRef.current) {
      return { success: false, reason: 'no_more' };
    }
    if (dateRangeSearch?.channelId === channel.id && dateRangeSearch?.messages != null) {
      return { success: false, reason: 'date_range' };
    }

    const channelMessages = getCurrentChannelMessages();
    const oldest = [...channelMessages].sort((a, b) => {
      const timeA = new Date(a.createdAt || a.timestamp || 0).getTime();
      const timeB = new Date(b.createdAt || b.timestamp || 0).getTime();
      return timeA - timeB;
    })[0];
        
        isLoadingOlderRef.current = true;
        setLoadingOlderMessages(true);
        
    const pageToLoad = olderPageRef.current;
    const messagesContainer = messagesContainerRef.current;
    const previousScrollHeight = messagesContainer?.scrollHeight ?? 0;
    const existingIds = new Set(channelMessages.map((m) => m.id));

    try {
      const result = await dispatch(loadOlderMessages({
            channelId: channel.id, 
        page: pageToLoad,
        size: INITIAL_PAGE_SIZE,
        beforeDate: oldest?.createdAt || null,
      }));

      if (result.type !== 'chat/loadOlderMessages/fulfilled') {
        console.error('Failed to load older messages:', result.payload);
        if (!isLoadingForNavigationRef.current && !result.payload?.fromRealm) {
          dispatch(addNotification({
            type: 'error',
            title: 'Could not load older messages',
            message: 'Scroll up again to retry.',
          }));
        }
        return { success: false, reason: 'failed' };
      }

      const loadedMessages = result.payload.messages || [];
      const newCount = loadedMessages.filter((m) => !existingIds.has(m.id)).length;

      olderPageRef.current = pageToLoad + 1;

      if (countChatBubbles(loadedMessages) < INITIAL_PAGE_SIZE) {
        setHasMoreMessages(false);
        duplicatePageStreakRef.current = 0;
      } else if (newCount === 0) {
        duplicatePageStreakRef.current += 1;
        if (duplicatePageStreakRef.current >= 3) {
                setHasMoreMessages(false);
        }
      } else {
        duplicatePageStreakRef.current = 0;
              }
              
      if (preserveScroll && !isLoadingForNavigationRef.current && newCount > 0 && messagesContainer) {
              setTimeout(() => {
                if (Date.now() < timelineSmoothJumpUntilRef.current) return;
                const newScrollHeight = messagesContainer.scrollHeight;
          const scrollDiff = newScrollHeight - previousScrollHeight;
                messagesContainer.scrollTop = scrollDiff;
              }, 50);
      }

      return { success: true, newCount };
    } finally {
          isLoadingOlderRef.current = false;
      setLoadingOlderMessages(false);
    }
  }, [channel.id, dateRangeSearch?.channelId, dateRangeSearch?.messages, dispatch, getCurrentChannelMessages]);

  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100; // Within 100px of bottom

      // Show/hide scroll to bottom button
      setShowScrollToBottom(!isNearBottom);
      showScrollToBottomRef.current = !isNearBottom;

      // Track whether user has left the bottom (do not mark true on every scroll tick from programmatic scroll alone)
      if (isNearBottom) {
        isUserScrollingRef.current = false;
      } else {
        isUserScrollingRef.current = true;
      }

      const isScrollable = scrollHeight > clientHeight + 20;
      const isNearTop = scrollTop < 100;
      const channelMessages = rawChannelMessagesRef.current;

      // Load older when user has scrolled away from bottom and reached the top.
      // Avoid firing on initial open (chat opens snapped to bottom, so isNearBottom is true).
      if (isScrollable &&
          !isNearBottom &&
          isNearTop &&
          channelMessages.length > 0 &&
          !isLoadingOlderRef.current &&
          Date.now() >= timelineSmoothJumpUntilRef.current &&
          hasMoreMessages &&
          !(dateRangeSearch?.channelId === channel.id && dateRangeSearch?.messages != null)) {
        loadOlderMessagesPage({ preserveScroll: true });
      }
    };

    messagesContainer.addEventListener('scroll', handleScroll);

    return () => {
      messagesContainer.removeEventListener('scroll', handleScroll);
    };
  }, [hasMoreMessages, channel.id, dateRangeSearch?.channelId, dateRangeSearch?.messages, loadOlderMessagesPage]);

  const scrollToMessageInContainer = (messageId, containerRef) => {
    const messageElement = messageRefs.current?.[messageId]
      || messageRefs.current?.[String(messageId)];
    const container = containerRef?.current;
    if (!messageElement || !container) return false;

    const delta = messageElement.getBoundingClientRect().top
      - container.getBoundingClientRect().top
      - (container.clientHeight / 2)
      + (messageElement.offsetHeight / 2);
    container.scrollTop += delta;
    highlightMessage(messageElement);
    return true;
  };

  const scrollToMessageWithRetry = async (messageId, containerRef, maxAttempts = 50) => {
    for (let i = 0; i < maxAttempts; i += 1) {
      if (scrollToMessageInContainer(messageId, containerRef)) {
        return true;
      }
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 50);
        });
      });
    }
    return false;
  };

  const updateScrollToBottomVisibility = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const awayFromBottom = scrollHeight - scrollTop - clientHeight >= 100;
    setShowScrollToBottom(awayFromBottom);
    showScrollToBottomRef.current = awayFromBottom;
  };

  const getThreadMessageIds = (threadId, msgs) => {
    if (!threadId) return new Set();
    const threadMessageIds = new Set([threadId]);
    let addedNew;
    do {
      addedNew = false;
      for (const m of msgs) {
        if (m.replyToMessageId && threadMessageIds.has(m.replyToMessageId) && !threadMessageIds.has(m.id)) {
          threadMessageIds.add(m.id);
          addedNew = true;
        }
      }
    } while (addedNew);
    return threadMessageIds;
  };

  const scrollToThreadMessage = (messageId) => {
    if (scrollToMessageInContainer(messageId, threadContainerRef)) {
      return true;
    }
    const threadId = activeThreadIdRef.current;
    const threadMessageIds = getThreadMessageIds(threadId, rawChannelMessagesRef.current);
    if (threadMessageIds.has(messageId)) {
      setTimeout(() => scrollToThreadMessage(messageId), 100);
      return false;
    }
    console.warn('Thread message not found in loaded messages:', messageId);
    return false;
  };

  const waitForInitialMessagesLoaded = async () => {
    for (let i = 0; i < 200; i += 1) {
      if (!store.getState().chat.loading) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  };

  const messageIsLoaded = (messageId, msgs) =>
    msgs.some((m) => String(m.id || m._id) === String(messageId));

  const resolveThreadRootId = (messageId, replyToMessageId, threadIdHint, msgs) => {
    if (threadIdHint) return threadIdHint;
    const message = msgs.find((m) => String(m.id || m._id) === String(messageId));
    const replyTo = replyToMessageId || message?.replyToMessageId;
    if (!replyTo) return null;
    if (message) {
      return getTopLevelParentId(message, msgs);
    }
    const parent = msgs.find((m) => String(m.id || m._id) === String(replyTo));
    if (parent) {
      return getTopLevelParentId(parent, msgs) || replyTo;
    }
    return replyTo;
  };

  const isNavigationTargetLoaded = (messageId, replyToMessageId, threadIdHint, msgs) => {
    const threadRootId = resolveThreadRootId(messageId, replyToMessageId, threadIdHint, msgs);
    if (threadRootId) {
      if (!messageIsLoaded(threadRootId, msgs)) return false;
      if (String(messageId) !== String(threadRootId) && !messageIsLoaded(messageId, msgs)) return false;
      const message = msgs.find((m) => String(m.id || m._id) === String(messageId));
      if (message) {
        const resolvedRoot = getTopLevelParentId(message, msgs);
        if (resolvedRoot && !messageIsLoaded(resolvedRoot, msgs)) return false;
      }
      return true;
    }
    return messageIsLoaded(messageId, msgs);
  };

  const navigateToChannelMessage = useCallback(async ({
    messageId,
    replyToMessageId,
    threadId: threadIdHint,
    quiet = false,
  } = {}) => {
    if (!messageId || isDateRangeActive) return false;

    const generation = messageNavigationGenerationRef.current + 1;
    messageNavigationGenerationRef.current = generation;
    isLoadingForNavigationRef.current = true;
    isUserScrollingRef.current = true;
    pendingNavigationMessageIdRef.current = messageId;

    const warnMissing = (title, message) => {
      if (quiet) return;
      dispatch(addNotification({ type: 'warning', title, message }));
    };

    try {
      await waitForInitialMessagesLoaded();
      if (generation !== messageNavigationGenerationRef.current) return false;

      if (!isNavigationTargetLoaded(
        messageId,
        replyToMessageId,
        threadIdHint,
        getCurrentChannelMessages(),
      )) {
        const aroundResult = await dispatch(fetchMessagesAroundTarget({
          channelId: channel.id,
          messageId,
          before: 60,
          after: 20,
        }));
        if (aroundResult.type === 'chat/fetchMessagesAroundTarget/fulfilled') {
          const aroundMsgs = aroundResult.payload.messages || [];
          const foundTarget = aroundMsgs.some(
            (m) => String(m.id || m._id) === String(messageId),
          );
          if (foundTarget) {
            aroundContextRef.current = {
              active: true,
              hasMoreBefore: aroundResult.payload.hasMoreBefore,
              hasMoreAfter: aroundResult.payload.hasMoreAfter,
            };
            setHasMoreMessages(Boolean(aroundResult.payload.hasMoreBefore));
            olderPageRef.current = 1;
            duplicatePageStreakRef.current = 0;
            setShowScrollToBottom(Boolean(aroundResult.payload.hasMoreAfter));
            showScrollToBottomRef.current = Boolean(aroundResult.payload.hasMoreAfter);
            pendingScrollMessageIdRef.current = messageId;
          }
        }
      }

      // Cap history hunt — previously 100 pages made chat feel stuck.
      let attempts = 0;
      const maxAttempts = 8;

      while (
        attempts < maxAttempts &&
        hasMoreMessagesRef.current &&
        !isNavigationTargetLoaded(
          messageId,
          replyToMessageId,
          threadIdHint,
          getCurrentChannelMessages(),
        )
      ) {
        const result = await loadOlderMessagesPage({ preserveScroll: false });
        if (!result?.success) break;
        attempts += 1;
        getCurrentChannelMessages();
        await new Promise((resolve) => setTimeout(resolve, 40));
        if (generation !== messageNavigationGenerationRef.current) return false;
      }

      const msgs = getCurrentChannelMessages();
      const threadRootId = resolveThreadRootId(messageId, replyToMessageId, threadIdHint, msgs);

      if (threadRootId) {
        if (!messageIsLoaded(threadRootId, msgs)) {
          warnMissing(
            'Message not found',
            'Could not load the conversation around this message.',
          );
          return false;
        }

        const landedOnRoot = await scrollToMessageWithRetry(threadRootId, messagesContainerRef);
        if (!landedOnRoot) return false;

        const targetMsg = msgs.find((m) => String(m.id || m._id) === String(messageId));
        const isReply = Boolean(
          threadIdHint
          || replyToMessageId
          || targetMsg?.replyToMessageId
        );
        if (!isReply || String(messageId) === String(threadRootId)) {
          updateScrollToBottomVisibility();
          return true;
        }

        setActiveThreadId(threadRootId);
        threadPinnedRef.current = true;
        setThreadPinned(true);
        await new Promise((resolve) => setTimeout(resolve, 400));
        if (generation !== messageNavigationGenerationRef.current) return false;

        if (String(messageId) !== String(threadRootId)) {
          await scrollToMessageWithRetry(messageId, threadContainerRef);
        }
        updateScrollToBottomVisibility();
        return true;
      }

      if (!messageIsLoaded(messageId, msgs)) {
        warnMissing(
          'Message not found',
          'Could not find this message in the chat history.',
        );
        return false;
      }

      const landed = await scrollToMessageWithRetry(messageId, messagesContainerRef);
      updateScrollToBottomVisibility();
      return landed;
    } catch (error) {
      console.error('Failed to navigate to message:', error);
      if (!quiet) {
        dispatch(addNotification({
          type: 'error',
          title: 'Could not open message',
          message: 'Something went wrong while loading the message. Please try again.',
        }));
      }
      return false;
    } finally {
      if (generation === messageNavigationGenerationRef.current) {
        isLoadingForNavigationRef.current = false;
        pendingNavigationMessageIdRef.current = null;
        pendingScrollMessageIdRef.current = null;
        isUserScrollingRef.current = true;
      }
    }
  }, [dispatch, getCurrentChannelMessages, isDateRangeActive, loadOlderMessagesPage, dailyTasksOpen, channel.id]);

  // Function to scroll to a specific message in the main channel list
  const scrollToMessage = (messageId, { replyToMessageId, skipHistoryLoad = false } = {}) => {
    if (scrollToMessageInContainer(messageId, messagesContainerRef)) {
      return;
    }
    const msgs = rawChannelMessagesRef.current;
    const targetInChannel = msgs.find((m) => String(m.id || m._id) === String(messageId));
    if (targetInChannel?.replyToMessageId) {
      const parentId = getTopLevelParentId(targetInChannel, msgs);
      if (parentId && String(parentId) !== String(messageId) && scrollToMessageInContainer(parentId, messagesContainerRef)) {
        return;
      }
    }
    const display = msgs.filter((m) => !m.replyToMessageId);
    const targetMessage = display.find((m) => String(m.id || m._id) === String(messageId))
      || (targetInChannel?.replyToMessageId
        ? display.find((m) => String(m.id || m._id) === String(getTopLevelParentId(targetInChannel, msgs)))
        : null);
      if (targetMessage) {
        setTimeout(() => {
        scrollToMessage(messageId, { replyToMessageId, skipHistoryLoad: true });
        }, 100);
      return;
    }
    if (!skipHistoryLoad && !isDateRangeActive) {
      navigateToChannelMessage({
        messageId,
        replyToMessageId: replyToMessageId ?? targetInChannel?.replyToMessageId,
      });
      return;
    }
    console.warn('Message not found in loaded messages:', messageId);
  };

  // Deep-link: open thread panel and/or scroll to a specific message (from URL params)
  useEffect(() => {
    if (!messageIdToScroll && !threadIdToOpen) {
      deepLinkNavKeyRef.current = null;
      return undefined;
    }
    if (loading || isDateRangeActive) return undefined;
    const deepLinkChannelId = searchParams.get('channelId');
    if (deepLinkChannelId && String(channel.id) !== String(deepLinkChannelId)) {
      return undefined;
    }

    // Include channel id so a leftover messageId from another chat cannot re-fire forever.
    const navKey = `${channel.id}:${messageIdToScroll || ''}:${threadIdToOpen || ''}`;
    if (deepLinkNavKeyRef.current === navKey) return undefined;
    deepLinkNavKeyRef.current = navKey;

    const clearNavParams = () => {
      setSearchParams((prev) => {
        if (!prev.get('messageId') && !prev.get('threadId') && !prev.get('channelId')) return prev;
        const next = new URLSearchParams(prev);
        next.delete('messageId');
        next.delete('threadId');
        next.delete('channelId');
        return next;
      }, { replace: true });
    };

    let cancelled = false;
    const scrollTargetId = messageIdToScroll || threadIdToOpen;
    const targetMessage = scrollTargetId
      ? rawChannelMessagesRef.current.find((m) => String(m.id || m._id) === String(scrollTargetId))
      : null;

    navigateToChannelMessage({
      messageId: messageIdToScroll || threadIdToOpen,
      replyToMessageId: targetMessage?.replyToMessageId,
      threadId: threadIdToOpen || undefined,
    }).then(() => {
      if (cancelled) return;
      // Always clear — stale params were causing "Message not found" on every later chat open.
      clearNavParams();
      pendingScrollMessageIdRef.current = null;
    });

    return () => {
      cancelled = true;
    };
  }, [
    messageIdToScroll,
    threadIdToOpen,
    loading,
    isDateRangeActive,
    navigateToChannelMessage,
    setSearchParams,
    channel.id,
    searchParams,
  ]);

  // Navigate to a workflow/task message after switching channels (Today's Tasks stays open).
  useEffect(() => {
    const pending = pendingDailyTasksNavRef.current;
    if (!pending || String(pending.channelId) !== String(channel?.id) || loading) return;

    pendingDailyTasksNavRef.current = null;
    navigateToChannelMessage({
      messageId: pending.messageId,
      replyToMessageId: pending.replyToMessageId,
      threadId: pending.threadId,
    });
  }, [channel?.id, loading, navigateToChannelMessage]);

  // Helper function to highlight a message
  const highlightMessage = (element) => {
    if (!element) return;
    
    // Add highlight classes
    element.classList.add('ring-2', 'ring-primary-500', 'ring-opacity-75', 'rounded-lg', 'transition-all', 'duration-300');
    element.style.transition = 'all 0.3s ease-in-out';
    
    // Remove highlight after 2 seconds
    setTimeout(() => {
      element.classList.remove('ring-2', 'ring-primary-500', 'ring-opacity-75', 'rounded-lg', 'transition-all', 'duration-300');
      element.style.transition = '';
    }, 2000);
  };

  // Mark messages as delivered / read when viewing direct messages
  useEffect(() => {
    // Only clear marked set when channel actually changes (not on every message length change)
    if (channel.id !== channelIdForReadRef.current) {
      markedAsReadRef.current.clear();
      channelIdForReadRef.current = channel.id;
    }

    if (isDirectMessage && displayMessages.length > 0 && user?.id) {
      const peerMessages = displayMessages.filter((msg) => String(msg.senderId) !== String(user.id));

      peerMessages.forEach((msg) => {
        acknowledgeMessageDelivered(msg, user.id);
      });

      // Mark all unread messages from the other user as read
      const unreadMessages = peerMessages.filter(msg => 
        !(msg.readBy || []).some((id) => String(id) === String(user.id)) &&
        !markedAsReadRef.current.has(msg.id) // Only mark if not already marked
      );
      
      if (unreadMessages.length > 0) {
        unreadMessages.forEach(async (msg) => {
          // Mark as being processed to prevent duplicate calls
          markedAsReadRef.current.add(msg.id);
          
          try {
            const token = localStorage.getItem('token');
            await axios.put(
              `${API_URL}/messages/${msg.id}/read`,
              {},
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
          } catch (error) {
            console.error('Error marking message as read:', error);
            // Remove from set if it failed so we can retry
            markedAsReadRef.current.delete(msg.id);
          }
        });
      }
    }
  }, [displayMessages.length, isDirectMessage, user?.id, channel.id]); // Use length instead of full array to prevent infinite loops

  const statusText = isAttendanceChannel
    ? 'Your attendance progress'
    : isNotificationsChannel
    ? 'Your assignments and status updates'
    : isWorkloopChannel
    ? 'Platform announcements'
    : isDirectMessage
    ? formatPresenceLabel(otherUser)
    : `${channel.memberIds?.length || 0} members`;

  const headerActionButtonClass = 'inline-flex h-9 w-9 items-center justify-center rounded-xl border border-transparent bg-white/70 dark:bg-gray-800/70 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/60 transition-all';

  return (
    <div className="flex h-full bg-white dark:bg-gray-800 overflow-x-hidden">
      {/* Main Chat Area */}
      <div className={`flex flex-col flex-1 h-full overflow-x-hidden min-w-0 ${
        (metricsExpanded && resolvedRightPanel === 'managementMetrics')
          || (eventsExpanded && resolvedRightPanel === 'events')
          || (filesExpanded && resolvedRightPanel === 'files')
            ? 'hidden'
            : ''
      }`}>
        {/* Header */}
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 px-3 py-3 sm:px-4 sm:py-3.5 border-b border-gray-200/90 dark:border-gray-700/90 bg-white/95 dark:bg-gray-800/95 backdrop-blur supports-[backdrop-filter]:bg-white/85 supports-[backdrop-filter]:dark:bg-gray-800/85 shadow-sm overflow-visible">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Mobile: open sidebar when closed */}
            {onToggleSidebar && !sidebarOpen && (
              <button
                type="button"
                className="lg:hidden flex-shrink-0 inline-flex h-9 w-9 items-center justify-center -ml-1 rounded-xl border border-transparent bg-white/70 dark:bg-gray-800/70 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/60 transition-all"
                onClick={onToggleSidebar}
                aria-label="Open menu"
              >
                <MenuIcon className="w-5 h-5" />
              </button>
            )}
            <div
              role={canViewProfile ? 'button' : undefined}
              tabIndex={canViewProfile ? 0 : undefined}
              onClick={() => canViewProfile && setShowUserProfile(true)}
              onKeyDown={(e) => {
                if (!canViewProfile) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowUserProfile(true);
                }
              }}
              className={`flex items-center gap-3 min-w-0 flex-1 text-left rounded-xl transition-colors ${
                canViewProfile
                  ? 'cursor-pointer hover:bg-gray-100/80 dark:hover:bg-gray-700/50 -my-0.5 py-0.5 pr-2'
                  : 'cursor-default'
              }`}
              aria-label={canViewProfile && otherUser ? `View ${otherUser.firstName} ${otherUser.lastName}'s profile` : undefined}
            >
            <div className="relative flex-shrink-0">
              {isWorkloopChannel ? (
                <div
                  className="w-9 h-9 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm font-bold ring-1 ring-primary-400/50"
                  title={workloopLabel}
                >
                  {workloopInboxInitial(workloopLabel)}
              </div>
              ) : isAttendanceChannel ? (
                <div
                  className="w-9 h-9 bg-emerald-600 rounded-full flex items-center justify-center text-white text-sm font-bold ring-1 ring-emerald-400/50"
                  title="Attendance"
                >
                  A
                </div>
              ) : isNotificationsChannel ? (
                <div
                  className="w-9 h-9 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-bold ring-1 ring-indigo-400/50"
                  title="Notifications"
                >
                  N
                </div>
              ) : (
              <PresignedAvatar
                fileKey={isDirectMessage ? otherUser?.profilePicture : channel.imageUrl}
                name={isDirectMessage && otherUser ? `${otherUser.firstName} ${otherUser.lastName}` : channel.name}
                initials={
                  isDirectMessage && otherUser
                    ? `${otherUser.firstName?.[0] || ''}${otherUser.lastName?.[0] || ''}`
                    : isDirectMessage
                      ? 'DM'
                      : (channel.name?.substring(0, 2).toUpperCase() || '#')
                }
                className="w-9 h-9 rounded-full object-cover relative ring-1 ring-gray-200 dark:ring-gray-700"
                fallbackClassName="w-9 h-9 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-semibold relative ring-1 ring-primary-400/50"
              />
              )}
              {!isWorkloopChannel && isDirectMessage && otherUser && (
                <div
                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 border-2 border-white dark:border-gray-800 rounded-full shadow-sm ${presenceDotClass(otherUser)}`}
                  title={formatPresenceLabel(otherUser)}
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 min-w-0">
                <h2 className={`text-base sm:text-lg truncate ${isAttendanceChannel ? 'font-bold text-emerald-700 dark:text-emerald-300' : isNotificationsChannel ? 'font-bold text-indigo-700 dark:text-indigo-300' : 'font-semibold text-gray-900 dark:text-white'}`}>
                  {isWorkloopChannel
                    ? workloopLabel
                    : isAttendanceChannel
                    ? 'Attendance'
                    : isNotificationsChannel
                    ? 'Notifications'
                    : isDirectMessage && otherUser
                    ? `${otherUser.firstName} ${otherUser.lastName}`
                    : channel.name}
                </h2>
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                  {statusText}
                </p>
                {!isAttendanceChannel && (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showChannelNotifications}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const next = !showChannelNotifications;
                      setChannelNotificationsVisible(channel.id, next);
                      setShowChannelNotifications(next);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="group/notif relative inline-flex flex-shrink-0 items-center"
                    title="Notification toggle"
                    aria-label="Notification toggle"
                  >
                    <span
                      className={`relative h-3 w-5 rounded-full transition-colors ${
                        showChannelNotifications ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-2 w-2 rounded-full bg-white shadow-sm transition-transform ${
                          showChannelNotifications ? 'translate-x-2.5' : 'translate-x-0.5'
                        }`}
                      />
                    </span>
                    <span className="pointer-events-none absolute left-1/2 top-full z-[60] mt-1 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm group-hover/notif:block dark:bg-gray-700">
                      Notification toggle
                    </span>
                  </button>
                )}
              </div>
            </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 overflow-visible">
            {!isReadOnlyChannel && (
              <ChannelCallMenu
                activeMeeting={activeChannelMeeting}
                isOrganizer={isCallOrganizer}
                headerActionButtonClass={`${headerActionButtonClass} relative`}
                onStartDirectCall={async () => {
                  if (startingDirectCall) return;
                  setStartingDirectCall(true);
                  try {
                    const message = await startDirectCall(channel.id, 'Direct call');
                    handleMeetingMessageUpdated(message);
                    const opened = openLiveKitCallWindow({
                      channelId: channel.id,
                      messageId: message.id,
                      roomName: getLiveKitRoomName(message.meetingInfo),
                      title: message.meetingInfo?.label || 'Direct call',
                    });
                    if (!opened) {
                      dispatch(addNotification({
                        type: 'error',
                        message: 'Could not open the call window. Try starting the call again.',
                      }));
                    }
                  } catch (err) {
                    const msg = err?.response?.data?.error || err?.response?.data || err?.message;
                    dispatch(addNotification({
                      type: 'error',
                      message: typeof msg === 'string'
                        ? msg.replace(/^Error:\s*/i, '')
                        : 'Failed to start direct call. Check LiveKit server config.',
                    }));
                  } finally {
                    setStartingDirectCall(false);
                  }
                }}
                onStartConnectMeeting={() => setShowConnectMeeting(true)}
                onJoinCall={() => {
                  const meeting = activeChannelMeeting?.meetingInfo;
                  if (isDirectCallMeeting(meeting)) {
                    const opened = openLiveKitCallWindow({
                      channelId: channel.id,
                      messageId: activeChannelMeeting.id,
                      roomName: getLiveKitRoomName(meeting),
                      title: meeting?.label || 'Direct call',
                    });
                    if (!opened) {
                      dispatch(addNotification({
                        type: 'error',
                        message: 'Could not open the call window. Try starting the call again.',
                      }));
                    }
                    return;
                  }
                  const url = meeting?.joinUrl;
                  if (url && !url.startsWith('workloop://')) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                    recordCallJoin({
                      title: meeting?.label || 'Call',
                      channelId: channel.id,
                      messageId: activeChannelMeeting?.id,
                      provider: meeting?.provider,
                      liveSession: false,
                    }).catch(() => {});
                  }
                }}
                onAddCallNotes={() => {
                  if (!activeChannelMeeting?.id) return;
                  setExpandCallNotesMeetingId(activeChannelMeeting.id);
                  setTimeout(() => {
                    const el = messageRefs.current[activeChannelMeeting.id];
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 100);
                }}
                onCloseCall={() => setShowCloseCall(true)}
                onStartNewCall={() => {
                  dispatch(addNotification({
                    type: 'warning',
                    message: 'Close the current call before starting a new one.',
                  }));
                }}
              />
            )}
            <button
              onClick={() => setShowInChatSearch((open) => !open)}
              className={`${headerActionButtonClass} ${showInChatSearch ? '!bg-gray-100 dark:!bg-gray-700 text-gray-800 dark:text-gray-100' : ''}`}
              title="Search in conversation"
              aria-label="Search in conversation"
              aria-pressed={showInChatSearch}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            {isAttendanceChannel && hasTeamAttendanceShares && (
              <button
                onClick={() => {
                  setActiveThreadId(null);
                  if (typeof setDailyTasksOpen === 'function') setDailyTasksOpen(false);
                  if (typeof setCalendarOpen === 'function') setCalendarOpen(false);
                  setActiveRightPanel(resolvedRightPanel === 'teamAttendance' ? null : 'teamAttendance');
                }}
                className={`${headerActionButtonClass} ${resolvedRightPanel === 'teamAttendance' ? '!bg-emerald-100 dark:!bg-emerald-900/40 text-emerald-800 dark:text-emerald-200' : ''}`}
                title="Team Attendance"
                aria-label="Team Attendance"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </button>
            )}
            {isDirectMessage && dmAttendanceShared && (
              <>
                <button
                  onClick={openDmUserWorkload}
                  className={`${headerActionButtonClass} ${dailyTasksOpen ? '!bg-emerald-100 dark:!bg-emerald-900/40 text-emerald-800 dark:text-emerald-200' : ''}`}
                  title="Workload"
                  aria-label="Open user workload"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </button>
                <button
                  onClick={() => openDmDayTimeline()}
                  disabled={openingDmTimeline}
                  className={`${headerActionButtonClass} ${dmDayTimeline ? '!bg-slate-200 dark:!bg-slate-700 text-slate-800 dark:text-slate-100' : ''} disabled:opacity-60`}
                  title="Day timeline"
                  aria-label="Day timeline"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h7" />
                  </svg>
                </button>
              </>
            )}
            {canUseCalendar && (
              <button
                onClick={() => {
                  setActiveThreadId(null);
                  if (typeof setDailyTasksOpen === 'function') setDailyTasksOpen(false);
                  if (typeof setCalendarOpen === 'function') setCalendarOpen(false);
                  setActiveRightPanel(activeRightPanel === 'calendar' ? null : 'calendar');
                }}
                className={`${headerActionButtonClass} ${activeRightPanel === 'calendar' ? '!bg-gray-100 dark:!bg-gray-700 text-gray-800 dark:text-gray-100' : ''}`}
                title="Calendar & appointments"
                aria-label="Calendar & appointments"
              >
                <CalendarIcon className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => {
                const currentPreference = soundPreferences[channel.id] !== undefined ? soundPreferences[channel.id] : true;
                const newPreference = !currentPreference;
                dispatch(setChannelSoundPreference({ channelId: channel.id, enabled: newPreference }));
              }}
              className={headerActionButtonClass}
              title={(soundPreferences[channel.id] !== undefined ? soundPreferences[channel.id] : true) ? "Mute notifications" : "Unmute notifications"}
              aria-label={(soundPreferences[channel.id] !== undefined ? soundPreferences[channel.id] : true) ? "Mute notifications" : "Unmute notifications"}
            >
              {(soundPreferences[channel.id] !== undefined ? soundPreferences[channel.id] : true) ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              )}
            </button>
            {!isDirectMessage && !isNotificationsChannel && (
              <button
                onClick={() => {
                  setActiveThreadId(null);
                  setActiveRightPanel(
                    activeRightPanel === 'channelDetails' ? null : 'channelDetails'
                  );
                }}
                className={`${headerActionButtonClass} ${activeRightPanel === 'channelDetails' ? '!bg-gray-100 dark:!bg-gray-700 text-gray-800 dark:text-gray-100' : ''}`}
                title="View members"
                aria-label="View members"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>
            )}
            {!isAttendanceChannel && !isNotificationsChannel && (
            <button
              onClick={() => {
                setActiveThreadId(null);
                setActiveRightPanel(
                  activeRightPanel === 'savedMessages' ? null : 'savedMessages'
                );
              }}
              className={`${headerActionButtonClass} relative ${activeRightPanel === 'savedMessages' ? '!bg-gray-100 dark:!bg-gray-700 text-gray-800 dark:text-gray-100' : ''}`}
              title="Bookmarks in this chat"
              aria-label="Bookmarks in this chat"
            >
              <BookmarkIcon className="w-5 h-5" />
              {channelSavedCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-primary-500 text-white text-[10px] font-semibold flex items-center justify-center">
                  {channelSavedCount > 99 ? '99+' : channelSavedCount}
                </span>
              )}
            </button>
            )}
            {!isAttendanceChannel && !isNotificationsChannel && (
            <button
              onClick={() => {
                setActiveThreadId(null);
                setActiveRightPanel(activeRightPanel === 'files' ? null : 'files');
              }}
              className={`${headerActionButtonClass} ${activeRightPanel === 'files' ? '!bg-gray-100 dark:!bg-gray-700 text-gray-800 dark:text-gray-100' : ''}`}
              title="Shared resources"
              aria-label="Shared resources"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            )}
            {!isAttendanceChannel && !isNotificationsChannel && (
            <button
              onClick={() => {
                setActiveThreadId(null);
                setActiveRightPanel(activeRightPanel === 'managementMetrics' ? null : 'managementMetrics');
              }}
              className={`${headerActionButtonClass} ${activeRightPanel === 'managementMetrics' ? '!bg-gray-100 dark:!bg-gray-700 text-gray-800 dark:text-gray-100' : ''}`}
              title="Management metrics"
              aria-label="Management metrics"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </button>
            )}
            {!isAttendanceChannel && (
            <button
              onClick={() => {
                setActiveThreadId(null);
                setActiveRightPanel(activeRightPanel === 'events' ? null : 'events');
              }}
              className={`${headerActionButtonClass} ${activeRightPanel === 'events' ? '!bg-gray-100 dark:!bg-gray-700 text-gray-800 dark:text-gray-100' : ''}`}
              title="Events"
              aria-label="Events"
            >
              <EventsIcon className="w-5 h-5" />
            </button>
            )}
            {hasMenuOptions && (
            <div className="relative z-40" ref={menuRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                className={headerActionButtonClass}
                title="More options"
                aria-label="More options"
              >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 w-52 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl ring-1 ring-black/5 dark:ring-white/10 p-1 z-[100]">
                  {canViewProfile && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowUserProfile(true);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>View Profile</span>
                    </button>
                  )}
                  {canUseCalendar && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveThreadId(null);
                        if (typeof setDailyTasksOpen === 'function') setDailyTasksOpen(false);
                        if (typeof setCalendarOpen === 'function') setCalendarOpen(false);
                        setActiveRightPanel('calendar');
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Calendar</span>
                    </button>
                  )}
                  {canEditChannel && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEditChannel(true);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Edit Channel</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
        </div>

        <ChatInChannelSearchPanel
          isOpen={showInChatSearch}
          query={inChatSearchQuery}
          mentionsOnly={inChatMentionsOnly}
          fromUserId={inChatSearchFromUserId}
          channelUsers={channelSearchUsers}
          matchCount={searchMatchIds.length}
          activeMatchIndex={activeSearchMatchIndex}
          dateRangeLoading={Boolean(dateRangeSearch?.loading && dateRangeSearch?.channelId === channel.id)}
          onQueryChange={setInChatSearchQuery}
          onMentionsOnlyChange={setInChatMentionsOnly}
          onFromUserIdChange={setInChatSearchFromUserId}
          onPrevMatch={handlePrevSearchMatch}
          onNextMatch={handleNextSearchMatch}
          onClearFilters={handleClearInChatSearchFilters}
          onDateRangeSearch={handleDateRangeSearch}
          onClose={handleCloseInChatSearch}
        />

        {/* Messages */}
        <div className="flex-1 relative overflow-hidden chat-window-pattern z-0 flex flex-col">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 dark:hidden"
          style={{
            backgroundImage: `url(${doodlePattern})`,
            backgroundSize: '210px 210px',
            backgroundRepeat: 'repeat',
            backgroundPosition: '0 0',
            opacity: 1,
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 hidden dark:block"
          style={{
            backgroundImage: `url(${doodlePattern})`,
            backgroundSize: '220px 220px',
            backgroundRepeat: 'repeat',
            backgroundPosition: '0 0',
            opacity: 0.33,
            filter: 'invert(1) contrast(1.2)',
          }}
        />

        <div 
          ref={messagesContainerRef}
          data-chat-timeline="true"
          tabIndex={-1}
          onMouseDown={(e) => {
            if (e.target.closest('input, textarea, select, button, a, [contenteditable="true"]')) return;
            messagesContainerRef.current?.focus({ preventScroll: true });
          }}
          className="relative z-10 flex-1 h-full overflow-y-auto overflow-x-hidden px-3 py-4 sm:px-6 sm:py-5 min-w-0 outline-none"
        >
          {chatMappingTarget && (
            <div className="sticky top-0 z-40 mb-3 flex items-center justify-between gap-2 rounded-lg border border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-950/40 px-3 py-2 text-xs text-yellow-900 dark:text-yellow-100 shadow-sm">
              <span className="font-medium">
                {chatMappingTarget.type === 'workflow'
                  ? 'Click a chat message to map it to this workflow'
                  : 'Click a chat message to map it to this task'}
              </span>
              <button
                type="button"
                onClick={() => dispatch(clearChatMappingTarget())}
                className="flex-shrink-0 px-2 py-1 rounded-md border border-yellow-400/60 dark:border-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
          {isDateRangeActive && (
            <div className="flex items-center justify-between gap-2 py-2.5 px-3.5 mb-4 rounded-xl bg-white dark:bg-gray-800 border border-primary-100 dark:border-primary-900 shadow-sm">
              <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
                Showing {dateRangeSearch.messages?.length ?? 0} message(s) from {dateRangeSearch.startDate ? new Date(dateRangeSearch.startDate).toLocaleDateString() : ''} to {dateRangeSearch.endDate ? new Date(dateRangeSearch.endDate).toLocaleDateString() : ''}
                {dateRangeSearch.userId ? ' (filtered by user)' : ''}
              </span>
              <button
                type="button"
                onClick={() => dispatch(clearDateRangeSearch())}
                className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
              >
                Clear filter
              </button>
            </div>
          )}
          {loadingOlderMessages && (
            <div className="flex justify-center py-4">
              <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-full shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500 animate-pulse" />
                Loading older messages...
              </div>
            </div>
          )}
          <div ref={timelineContentRef}>
          <MessageList 
            messages={messagesForList} 
            currentUserId={user?.id}
            isDirectMessage={isDirectMessage}
            channelId={channel?.id}
            onAddReaction={handleAddReaction}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
            onReply={(message) => {
              const parentId = getTopLevelParentId(message, rawChannelMessages);
              openPinnedThread(parentId);
              if (message.replyToMessageId) {
                setReplyingToMessage(message);
              } else {
                setReplyingToMessage(null);
              }
            }}
            onViewThread={(messageId) => {
              const message = rawChannelMessages.find(m => m.id === messageId);
              const parentId = message ? getTopLevelParentId(message, rawChannelMessages) : messageId;
              if (activeThreadId === parentId && threadPinned) {
                closeThreadPanel();
              } else {
                openPinnedThread(parentId);
              }
              setReplyingToMessage(null);
            }}
            onPreviewThread={(messageId) => {
              if (!messageId) {
                previewThread(null);
                return;
              }
              const message = rawChannelMessages.find(m => m.id === messageId);
              const parentId = message ? getTopLevelParentId(message, rawChannelMessages) : messageId;
              previewThread(parentId);
            }}
            onCancelThreadHoverOpen={() => {
              clearThreadHoverOpenTimer();
            }}
              onForward={(message) => {
                setMessageToForward(message);
                setShowForwardModal(true);
              }}
            onScrollToMessage={scrollToMessage}
            onJumpTimelineUp={jumpTimelineUp}
            onJumpTimelineDown={jumpTimelineDown}
            messageRefs={messageRefs}
              onRetryMessage={handleRetryMessage}
            onMeetingMessageUpdated={handleMeetingMessageUpdated}
            onEditEvent={handleEditEventFromTimeline}
            onCloneEvent={handleCloneEventFromTimeline}
            cloningEventId={cloningEventId}
            expandCallNotesMeetingId={expandCallNotesMeetingId}
            onJoinDirectCall={(message) => {
              const meeting = message?.meetingInfo;
              if (!isDirectCallMeeting(meeting)) return;
              const opened = openLiveKitCallWindow({
                channelId: channel.id,
                messageId: message.id,
                roomName: getLiveKitRoomName(meeting),
                title: meeting?.label || 'Direct call',
              });
              if (!opened) {
                dispatch(addNotification({
                  type: 'error',
                  message: 'Could not open the call window. Try starting the call again.',
                }));
              }
            }}
            reminderPanelOpen={dailyTasksOpen}
            allChannelMessages={rawChannelMessages}
            channelId={channel.id}
            searchHighlightQuery={searchHighlightQuery}
            activeSearchMatchMessageId={activeSearchMatchMessageId}
            assignableUsers={assignableUsers}
            chatMappingActive={Boolean(chatMappingTarget)}
            onMapMessage={handleMapMessageToTarget}
            chatStartLabel={chatStartLabel}
            emptyHint={isAttendanceChannel ? {
              title: 'No attendance entries yet',
              description: 'Use Logging to Work or Logout Now below — only your progress appears here.',
            } : isNotificationsChannel ? {
              title: showChannelNotifications ? 'No notifications yet' : 'Notifications hidden',
              description: showChannelNotifications
                ? 'Assignments and status changes for you will appear here. Click a line to open that ticket or task.'
                : 'Turn Notifications on in the header to see assignment and status lines on this device.',
            } : !showChannelNotifications ? {
              title: 'Chat looks quieter',
              description: 'Assignment and status lines are hidden in this channel. Turn Notifications on in the header to show them again.',
            } : null}
          />
          {templatePosting?.channelId === channel.id ? (
            <div className="flex justify-end mt-3 pr-1">
              <div
                className="inline-flex items-center gap-2 max-w-[85%] rounded-2xl border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-950/40 px-3.5 py-2.5 text-sm font-medium text-primary-800 dark:text-primary-100 shadow-sm"
                role="status"
                aria-live="polite"
              >
                <svg className="h-4 w-4 animate-spin flex-shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
                </svg>
                {templatePosting.label || 'Creating workflow…'}
              </div>
            </div>
          ) : null}
          <TypingIndicator 
            typingUsers={typingUsers[channel.id] || []} 
            currentUser={user?.username}
          />
          <div ref={messagesEndRef} />
          </div>
        </div>

          {/* Scroll to Bottom Button */}
          {showScrollToBottom && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-20 right-4 sm:right-6 z-10 bg-primary-500 hover:bg-primary-600 text-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              title="Scroll to bottom"
              aria-label="Scroll to bottom"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </button>
          )}
        </div>

        {isWorkloopChannel && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 text-center text-sm text-gray-600 dark:text-gray-400">
            Read-only — messages from {workloopLabel} appear here.
          </div>
        )}

        {isNotificationsChannel && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 text-center text-sm text-gray-600 dark:text-gray-400">
            Only you can see this channel. Click a line to open the ticket or task.
          </div>
        )}

        {isAttendanceChannel && (
          <AttendanceActions
            user={user}
            channelId={channel.id}
            onOpenTeamAttendance={() => {
              if (hasTeamAttendanceShares) {
                setActiveRightPanel('teamAttendance');
              }
            }}
            onAttendanceMessagePosted={(message) => {
              if (message) {
                dispatch(addMessage({
                  channelId: channel.id,
                  message,
                  currentUserId: user?.id,
                }));
              }
              dispatch(fetchMessages({ channelId: channel.id, page: 0 }));
            }}
          />
        )}

        {/* Message Input - hidden for read-only channels */}
        {!isReadOnlyChannel && (
        <MessageInput 
            key={`main-${channel.id}`}
            onSendMessage={(content, attachments, replyToMessageId, linkPreview, mentionedUserIds, attachmentGroupName, targetResourceGroupId, sensitiveMeta) =>
              handleSendMessage(content, attachments, replyToMessageId, linkPreview, mentionedUserIds, attachmentGroupName, targetResourceGroupId, sensitiveMeta)
            }
          onTyping={handleTyping}
          channelId={channel.id}
            isThreadInput={false}
            onTemplatePostingChange={(next) => {
              setTemplatePosting(next);
              if (next) window.setTimeout(() => scrollToBottom(), 0);
            }}
            onCreateEvent={() => {
              setEditingEvent(null);
              setShowEventWizard(true);
            }}
        />
        )}
      </div>

      {/* Unified Right Sidebar for Threads, Daily Tasks, Conversation Calendar, and Files.
          Thread (reply) takes priority over Today's Tasks so the reply bar stays visible. */}
      <RightSidebar
        panelType={
          activeThreadId
            ? 'thread'
            : (resolvedRightPanel
              ? resolvedRightPanel
              : (dailyTasksOpen ? 'dailyTasks' : null))
        }
        collapsed={rightPanelCollapsed}
        onToggleCollapse={() => {
          setRightPanelCollapsed((collapsed) => {
            const next = !collapsed;
            if (next && activeThreadId && !threadPinnedRef.current) {
              threadPinnedRef.current = true;
              setThreadPinned(true);
            }
            return next;
          });
        }}
        className={
          !rightPanelCollapsed && (
            (metricsExpanded && resolvedRightPanel === 'managementMetrics')
            || (eventsExpanded && resolvedRightPanel === 'events')
            || (filesExpanded && resolvedRightPanel === 'files')
          )
            ? 'lg:!w-full'
            : ''
        }
        panelProps={(() => {
          if (activeThreadId) {
            return {
              channel,
              user,
              isReadOnlyChannel,
              activeThreadId,
              rawChannelMessages,
              handleSendMessage,
              handleTyping,
              handleAddReaction,
              handleEditMessage,
              handleDeleteMessage,
              handleRetryMessage,
              scrollToMessage: scrollToThreadMessage,
              messageRefs,
              containerRef: threadContainerRef,
              setMessageToForward,
              setShowForwardModal,
              initialReplyingToMessage: replyingToMessage,
              dailyTasksOpen,
              isPreview: !threadPinned,
            };
          }
          if (resolvedRightPanel === 'files') {
            return {
              channel,
              isExpanded: filesExpanded,
              onToggleExpand: () => setFilesExpanded((open) => !open),
            };
          }
          if (resolvedRightPanel === 'calendar') {
            return { channel, otherUser, personalMode: false };
          }
          if (resolvedRightPanel === 'teamAttendance') {
            return {};
          }
          if (resolvedRightPanel === 'savedMessages') {
            return {
              channel,
              onMessageClick: (messageId) => {
                scrollToMessage(messageId);
              },
            };
          }
          if (resolvedRightPanel === 'channelDetails') {
            return { channel };
          }
          if (resolvedRightPanel === 'managementMetrics') {
            return {
              channel,
              isExpanded: metricsExpanded,
              onToggleExpand: () => setMetricsExpanded((open) => !open),
              onMessageClick: (metricItem) => {
                if (!metricItem?.messageId) return;
                setMetricsExpanded(false);
                navigateToChannelMessage({
                  messageId: metricItem.messageId,
                  replyToMessageId: metricItem.replyToMessageId,
                });
              },
            };
          }
          if (resolvedRightPanel === 'events') {
            return {
              channel,
              otherUser,
              isExpanded: eventsExpanded,
              onToggleExpand: () => setEventsExpanded((open) => !open),
            };
          }
          if (dailyTasksOpen) {
            return {
              onNavigateToMessage: (item) => {
                if (!item?.messageId || !item?.channelId) return;

                if (String(item.channelId) === String(channel.id)) {
                  navigateToChannelMessage({
                    messageId: item.messageId,
                    replyToMessageId: item.replyToMessageId,
                    threadId: item.threadId || (item.openThread ? item.messageId : undefined),
                  });
                  return;
                }

                const state = store.getState();
                const allChannels = [
                  ...(state.channels?.channels || []),
                  ...(state.channels?.directMessages || []),
                ];
                const targetChannel = allChannels.find((ch) => String(ch.id) === String(item.channelId));
                if (!targetChannel) {
                  dispatch(addNotification({ type: 'error', message: 'Channel not found.' }));
                  return;
                }

                pendingDailyTasksNavRef.current = {
                  channelId: item.channelId,
                  messageId: item.messageId,
                  replyToMessageId: item.replyToMessageId,
                  threadId: item.threadId || (item.openThread ? item.messageId : undefined),
                };
                dispatch(setCurrentChannel(targetChannel));
              },
              onToggleExpand: onExpandDailyTasks,
              isExpanded: false,
              homeLayout: true,
              initialViewingUserId: workloadTargetUserId,
              onViewingUserChange: onOpenUserWorkload,
            };
          }
          return {};
        })()}
        onClose={() => {
          if (activeThreadId) {
            closeThreadPanel();
          } else if (resolvedRightPanel) {
            setActiveRightPanel(null);
            setRightPanelCollapsed(false);
          } else if (typeof setDailyTasksOpen === 'function') {
            setDailyTasksOpen(false);
            setRightPanelCollapsed(false);
          }
        }}
        onMouseEnter={() => {
          if (activeThreadId && !threadPinned) {
            clearThreadHoverCloseTimer();
          }
        }}
        onMouseLeave={() => {
          if (activeThreadId && !threadPinned) {
            previewThread(null);
          }
        }}
        onPointerDown={() => {
          if (activeThreadId && !threadPinned) {
            openPinnedThread(activeThreadId);
          }
        }}
      />

      {/* User Profile Modal */}
      {showUserProfile && isDirectMessage && otherUser && (
        <UserProfileModal
          userId={otherUser.id}
          isOpen={showUserProfile}
          onClose={() => setShowUserProfile(false)}
        />
      )}

      {dmDayStatus?.type === 'LEAVE' && (
        <SharedUserLeaveStatusModal
          userName={dmDayStatus.userName}
          leaveLabel={dmDayStatus.leaveLabel}
          dateLabel={dmDayStatus.dateLabel}
          dayLabel={dmDayStatus.dayLabel}
          onClose={() => setDmDayStatus(null)}
          onOpenTimeline={() => openDmDayTimeline()}
        />
      )}

      {dmDayStatus?.briefing && dmDayStatus?.type !== 'LEAVE' && (
        <LogToWorkModal
          briefing={dmDayStatus.briefing}
          userName={dmDayStatus.userName}
          readOnly
          completedTasks={dmDayStatus.completedTasks}
          comment={dmDayStatus.comment}
          completedMeta={dmDayStatus.completedMeta}
          headerSubtitle={dmDayStatus.headerSubtitle}
          dayStats={dmDayStatus.dayStats}
          onClose={() => setDmDayStatus(null)}
          onOpenTimeline={() => openDmDayTimeline(dmDayStatus.briefing?.date || dmDayStatus.day?.date)}
        />
      )}

      {dmDayTimeline && (
        <DayActivityTimelineModal
          timeline={dmDayTimeline}
          onClose={() => setDmDayTimeline(null)}
          onChangeDate={async (nextDate) => {
            if (!otherUser?.id) return;
            const data = await fetchDayActivityTimeline(nextDate, otherUser.id);
            setDmDayTimeline(data);
          }}
        />
      )}

      {/* Edit Channel Modal */}
      {showEditChannel && !isDirectMessage && (
        <EditChannelModal
          isOpen={showEditChannel}
          onClose={() => setShowEditChannel(false)}
          channel={channel} 
        />
      )}

      {/* Forward Message Modal */}
      <ForwardMessageModal
        isOpen={showForwardModal}
        onClose={() => {
          setShowForwardModal(false);
          setMessageToForward(null);
        }}
        onChannelSelected={(selectedChannel) => {
          // Automatically forward the message to the selected channel
          if (messageToForward) {
            handleForwardMessage(messageToForward, selectedChannel);
          }
          setShowForwardModal(false);
          setMessageToForward(null);
        }}
        messageToForward={messageToForward}
      />

      <ConnectMeetingModal
        isOpen={showConnectMeeting}
        onClose={() => setShowConnectMeeting(false)}
        channelId={channel.id}
        onMeetingStarted={(message) => {
          handleMeetingMessageUpdated(message);
        }}
      />

      <CreateEventWizard
        isOpen={showEventWizard}
        onClose={() => {
          setShowEventWizard(false);
          setEditingEvent(null);
        }}
        onCreated={(saved) => {
          if (!saved || saved.status === 'DRAFT' || !channel?.id) return;
          const eventInfo = eventDtoToTimelineInfo(saved);
          const now = new Date().toISOString();
          if (saved.chatMessageId && eventInfo?.eventId) {
            const timelineMessage = {
              id: saved.chatMessageId,
              content: saved.name
                ? `${eventInfo.eventType ? String(eventInfo.eventType).replace(/_/g, ' ') : 'Event'}: ${saved.name}`
                : '',
              senderId: user?.id,
              senderName: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username || 'You',
              senderProfilePicture: user?.profilePicture || null,
              channelId: channel.id,
              type: 'TEXT',
              attachments: [],
              reactions: [],
              eventInfo,
              createdAt: now,
              timestamp: now,
            };
            dispatch(addMessage({
              channelId: channel.id,
              message: timelineMessage,
              currentUserId: user?.id,
            }));
            upsertRealmDocs('Message', [timelineMessage]).catch(() => {});
            requestAnimationFrame(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            });
          }
          dispatch(fetchMessages({
            channelId: channel.id,
            page: 0,
            size: INITIAL_PAGE_SIZE,
            skipRealm: true,
          }));
        }}
        channel={channel}
        otherUser={otherUser}
        editingEvent={editingEvent}
      />

      <CloseMeetingDialog
        isOpen={showCloseCall}
        onClose={() => setShowCloseCall(false)}
        onSubmit={handleCloseCall}
        meetingLabel={activeChannelMeeting?.meetingInfo?.label}
        noteMessages={activeCallNoteMessages}
        loading={closingCall}
      />
      {completionTimeDialog}
      {urgentDeadlineDialog}
      <CreateWorkflowDialog
        isOpen={Boolean(createWorkflowDraft)}
        typeId={createWorkflowDraft?.typeId}
        initialDescription={createWorkflowDraft?.initialDescription || ''}
        saving={creatingWorkflow}
        defaultSpaceId={channel?.spaceId || ''}
        defaultSpaceName={channel?.spaceName || ''}
        onClose={() => {
          if (creatingWorkflow) return;
          setCreateWorkflowDraft(null);
        }}
        onCreate={handleCreateWorkflowFromChat}
      />
    </div>
  );
};

export default ChatWindow;

